'use client'

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { datadogLogs } from '@datadog/browser-logs'
import { fromInteger } from '@repo/money'
import {
  generateChipsFromMultipliers,
  generateSmartChips,
  hasCustomChipMultipliers,
  prependMinBetChip
} from 'src/utils/generateSmartChips'
import { formatNumberWithSuffix } from 'src/utils/formatNumberWithSuffix'
import { roundAmount } from 'src/utils/roundAmount'
import { useClientSideTranslation } from 'src/services/i18n'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import { useGetCurrentLanguage } from 'src/store/game'
import { useGetGameConfig } from 'src/store/game'
import { useGetCurrentBets } from 'src/hooks/useGetCurrentBets'
import { useBetStore } from 'src/store/bet'
import { usePlayerActions } from 'src/store/player'
import { useGetPlayerLevel, useIsLevelSystemEnabled } from 'src/store/playerLevel'
import type { AuthenticatedPlayer } from 'src/types/domain/Player'
import type { MarbleGame } from 'src/types/domain/Game'
import type { MarketConfig } from 'src/types/domain/GameConfig'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { useChipPlacementBetSubmit } from 'src/hooks/useChipPlacementBetSubmit'
import { useBetLock, type BetLockSent } from 'src/hooks/useBetLock'
import { RejectionDetails } from 'src/types/domain/AppError'
import { useStageTwo } from 'src/hooks/useStageTwo'
import { stageTwoAt } from 'src/utils/betLock'
import { logChipAfterLockOnce } from 'src/utils/hardCloseClickRefused'
import { useDeviceInfo } from 'src/hooks/useDeviceInfo'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { emitBalanceInsufficient } from 'src/services/postMessage/emitters'
import { getLatestGameEvent } from 'src/utils/raceUtils'
import { isBirdieGameType, isCCTVGameType } from 'src/utils/gameTypeUtils'
import { getCCTVCopy } from 'src/utils/cctvCopy'
import { isRaceSettledEvent, isRaceStartedEvent } from 'src/types/domain/RaceEvent'
import type { Bet } from 'src/types/domain/Bet'
import type { BetType } from 'src/types/domain/BetDetails'
import { WinPill } from './WinPill'
import { TotalWinBanner } from './TotalWinBanner'
import { SideBetRows } from './SideBetRows'
import { ChipStrip } from './ChipStrip'
import { resolveGameKey, sideBetMultiplier } from 'src/utils/effectiveRtp'
import { chargeableStake } from './chargeableStake'
import { isOwnRoundRerun, roundTransitionAction } from './roundTransitionAction'
import {
  isFrozenSubmission,
  classifySubmitOutcome,
  hardLockBoard,
  shouldRecoverMissedRoundReset,
  SUBMIT_TIMER_MS
} from './submissionLifecycle'
import {
  isBonus,
  isBooster,
  useInventoryItems,
  type Bonus,
  type Booster
} from 'src/features/inventory'
import LightningIcon from 'src/app/assets/icons/ico-bolt-lightning.svg'
import InfoCircleIcon from 'src/app/assets/icons/ico-info-circle.svg'
import './chip-placement.scss'

// Delay between `race_started` firing and the first reconciliation
// pass against `currentBets`. Matches the wall-clock grace inside
// the backend `BettingService.CanPlaceBet` (5s, see betting.go) so
// the FE only declares a silent failure after the server has had
// every opportunity to accept the bet. Tighten in lockstep if the
// backend grace is ever reduced.
const RACE_STARTED_RECONCILE_DELAY_MS = 5000

// Visual templates only — chip *values* are derived from the live
// game config + balance at runtime (see `chips` useMemo below).
// Cycled by index when the config gives us more chips than templates.
const CHIP_STYLES: Array<{ bg: string; border: string; color: string }> = [
  { bg: 'linear-gradient(160deg, #3d3d3d, #1a1a1a)', border: '#666666', color: '#bbb' },
  { bg: 'linear-gradient(160deg, #1a5c4c, #0a3325)', border: '#28b890', color: '#5eeac4' },
  { bg: 'linear-gradient(160deg, #2a4a8a, #142850)', border: '#4488dd', color: '#99ccff' },
  { bg: 'linear-gradient(160deg, #b83030, #6b1a1a)', border: '#e05050', color: '#ffbbbb' },
  { bg: 'linear-gradient(160deg, #c47b10, #7a4d08)', border: '#e8a020', color: '#ffe0a0' },
  { bg: 'linear-gradient(160deg, #1f8a1f, #0d4a0d)', border: '#30b030', color: '#90ee90' },
  { bg: 'linear-gradient(160deg, #1a1a1a, #000000)', border: '#9060d0', color: '#c8a8f0' }
]

// Teal accent applied to the first real chip when an extra min-bet chip is
// shown (QA-60081). The min chip takes the muted grey CHIP_STYLES[0]; this
// gives the first betting chip its own colour instead of leaving two greys,
// while the rest of the palette (up to the purple top chip) is unchanged.
const MIN_BET_CHIP_STYLE = {
  bg: 'linear-gradient(160deg, #155e63, #08363a)',
  border: '#2bb3bf',
  color: '#7fe6ef'
}

// Enrich market options with marble data (images, colors) from the game
function enrichMarkets(markets: MarketConfig[], game: MarbleGame): MarketConfig[] {
  const marbleMap = new Map(game.marbles.map((m) => [m.uuid, m]))
  return markets
    .filter((m) => m.options.some((o) => o.bettable))
    .map((market) => ({
      ...market,
      options: market.options
        .map((opt) => {
          const marble = marbleMap.get(opt.id)
          // Carry the marble number through the option as a hidden
          // sort key — primary multiplier sort still wins, but on ties
          // (Even/Odd at 50/50, Exact-Low/High at 2.5/2.5) the marble
          // number breaks the tie deterministically. Stripped before
          // the option ships to render so the type contract is clean.
          const sortKey = marble?.number ? parseInt(marble.number, 10) : Number.MAX_SAFE_INTEGER
          return {
            ...opt,
            name: opt.name || marble?.color || opt.id,
            color: opt.color || marble?.color?.toLowerCase() || '#666',
            image: opt.image || marble?.image || undefined,
            __sortKey: Number.isFinite(sortKey) ? sortKey : Number.MAX_SAFE_INTEGER
          } as MarketConfig['options'][number] & { __sortKey: number }
        })
        .sort((a, b) => {
          if (a.multiplier !== b.multiplier) return a.multiplier - b.multiplier
          return a.__sortKey - b.__sortKey
        })
        .map(({ __sortKey: _drop, ...rest }) => rest)
    }))
    .sort((a, b) => a.displayOrder - b.displayOrder)
}

interface PlacedChip {
  optionId: string
  marketId: string
  amount: number
  // Number of distinct click-placements on this option (NOT
  // amount/chip-value). Drives the chip-stack visualisation: the more
  // times the player clicked, the taller the stack — same way a real
  // roulette table reads at a glance. Doubling / repeating doesn't
  // change `count`; only direct chip placements do.
  count: number
  // Side-bet payload (forecast / tricast). Snapshotted on placement so
  // the chip carries everything needed to submit + display its odds
  // without re-deriving from `marbleOrder`. Undefined → standard
  // main-market PickWinner chip.
  betType?: BetType
  selectedItems?: string[]
  // Static multiplier captured when the side-bet chip was placed.
  // Used by `totalPotentialWin` and the chip's display, so the chip
  // shows the same number it was placed at even if marbleOrder changes.
  multiplier?: number
  // Human-readable label for the row, e.g. "Yellow → Red" (exact) or
  // "Yellow + Red" (any-order). Shown on the side-bet row, not on the
  // chip itself.
  label?: string
}

type ChipDefinition = { value: number; bg: string; border: string; color: string }

// Map a total amount to the nearest chip color (based on magnitude).
// Takes the live chip list because the values are config/balance-driven
// at runtime, not module-level constants.
function getChipStyleForAmount(
  amount: number,
  chips: ChipDefinition[]
): { bg: string; border: string } {
  const fallback = chips[0]
    ? { bg: chips[0].bg, border: chips[0].border }
    : { bg: CHIP_STYLES[0]!.bg, border: CHIP_STYLES[0]!.border }
  for (let i = chips.length - 1; i >= 0; i--) {
    const chip = chips[i]
    if (chip && amount >= chip.value) {
      return { bg: chip.bg, border: chip.border }
    }
  }
  return fallback
}

// Format chip amount: show decimals only when needed
function formatChipAmount(amount: number): string {
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}k`
  if (amount >= 100) return amount.toFixed(0)
  if (amount >= 10) return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(1)
  if (amount >= 1) return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(1)
  return amount.toFixed(1)
}

interface FlyingChip {
  id: string
  fromX: number
  fromY: number
  toX: number
  toY: number
  bg: string
  border: string
  amount: number
}

interface ChipPlacementStats {
  totalStaked: number
  totalPotentialWin: number
  chipCount: number
}

type WinTier = 'win' | 'big' | 'mega'

// Pure helper hoisted out of the component so it's not re-created
// on every render. Drives the WinPill size/glow tier + the bonus
// sound layering — see `topWinTier` for thresholds rationale.
function tierForBet(bet: Bet): WinTier {
  if (bet.status !== 'won' || bet.betAmount <= 0) return 'win'
  const ratio = bet.potentialPayout / bet.betAmount
  if (ratio >= 25) return 'mega'
  if (ratio >= 10) return 'big'
  return 'win'
}

type Props = Readonly<{
  game: MarbleGame
  player: AuthenticatedPlayer
  onStatsChange?: (stats: ChipPlacementStats) => void
}>

export function ChipPlacementPanel({ game, player, onStatsChange }: Props) {
  // Birdie: the odds are new every round, so there is nothing to "auto" —
  // the toggle is hidden and `autoBet` can never become true.
  const isBirdieGame = isBirdieGameType(game.track.gameType)
  const gameConfig = useGetGameConfig()
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const latestEvent = getLatestGameEvent(game)
  const { submitAllBets, isSubmitting } = useChipPlacementBetSubmit({ game, player })
  const { currentBets } = useGetCurrentBets()
  const { playEffect } = useSoundEffect()
  const { isMobile } = useDeviceInfo()
  const playerActions = usePlayerActions()
  const [selectedChipIdx, setSelectedChipIdx] = useState(2) // default $1
  const [placedChips, setPlacedChips] = useState<Record<string, PlacedChip>>({})
  // Undo history: a stack of board snapshots taken BEFORE each mutating
  // action (place / double / repeat). Undo pops the last snapshot and
  // restores it, so one undo reverses exactly one action — e.g. removing
  // just the last chip from a stacked option instead of wiping the whole
  // option (QA-60196). Auto-cleared whenever the board empties (clear-all,
  // round transition, undo-to-empty) by the effect below.
  const undoHistoryRef = useRef<
    Array<{
      chips: Record<string, PlacedChip>
      boosters: Record<string, { boosterId: string; percent: number }>
      freeBets: Record<string, { freeBetId: string; amount: number }>
    }>
  >([])
  const [flyingChips, setFlyingChips] = useState<FlyingChip[]>([])
  const [autoBet, setAutoBet] = useState(false)
  const [lastBets, setLastBets] = useState<Record<string, PlacedChip> | null>(null)
  // Snapshot of `appliedBoosters` taken at the same moment as
  // `lastBets`. Repeat replays the player's previous round verbatim,
  // and that includes which booster sat on which option — without
  // this snapshot, repeat falls back to "oldest booster → first
  // chip" which can shuffle assignments compared to the original
  // round. Stored as a parallel state instead of inside `lastBets`
  // because the booster pool itself can change between rounds (some
  // get consumed, new ones earned), so we still validate against
  // the live `availableBoosters` at repeat time.
  const [lastAppliedBoosters, setLastAppliedBoosters] = useState<
    Record<string, { boosterId: string; percent: number }>
  >({})
  // Submission lifecycle flags. Declared up here (not co-located with the
  // auto-bet pipeline below) because `totalPotentialWin` and the per-
  // option multiplier display read `betsSubmitted` to decide whether a
  // revealed lightning boost applies — chips on the board with
  // betsSubmitted=false are queued for the NEXT round, so the current
  // round's lightning state shouldn't factor in.
  const [betsSubmitted, setBetsSubmitted] = useState(false)
  const [betsActuallySent, setBetsActuallySent] = useState(false)
  // The round whose own bets the board was last rebuilt from (the restore
  // below). A hard round's lock never sends those chips again
  // (`hardLockBoard`): they are bets already.
  const restoredRoundIdRef = useRef<string | undefined>(undefined)
  // The option that JUST received a chip — drives the warm "depth-press
  // + glow" pulse when the flying chip lands. Cleared after ~800ms so
  // the animation can re-fire on the next click. Stored as a single id
  // (not a map) because only one chip lands at a time.
  const [receivingOption, setReceivingOption] = useState<string | null>(null)
  const receivingClearRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Timestamps used to drive one-shot CSS animations: setting them to
  // Date.now() applies a `--animating` class for ~500ms via the effects
  // below, then clears it so the animation can re-fire on the next click.
  const [doubleAnimAt, setDoubleAnimAt] = useState(0)
  const [autoSpinAt, setAutoSpinAt] = useState(0)
  // One-shot transient banner above the chip strip (e.g. "Max bet
  // reached"). Each call replaces any current message so the latest
  // feedback always wins, and the message clears itself after a couple
  // of seconds via the effect below.
  const [notice, setNotice] = useState<{
    message: string
    tone: 'warning' | 'success' | 'info'
    durationMs: number
  } | null>(null)

  // Inventory items are still read here for the auto-apply-booster
  // assignment logic below (binding boosters to placed chips). The
  // player's held free bets / boosters are surfaced in the right
  // sidebar's "Rewards" panel; free bets auto-apply on chip click.

  // Handed to `<ChipStrip>` so the flying chip can launch from the active
  // chip disc inside the bar.
  const chipSelectorRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  // High-watermark of the player's balance: grows when balance increases
  // (refresh-balance from the operator iframe, deposits, big wins), but
  // never shrinks. Without the no-shrink rule a single losing round
  // would strip the larger chips off the strip and confuse the player.
  const initialBalanceRef = useRef<number | null>(null)
  if (initialBalanceRef.current === null || player.balance.amount > initialBalanceRef.current) {
    initialBalanceRef.current = player.balance.amount
  }

  // Runtime chip list, driven by the live game config + the player's
  // initial balance instead of a hardcoded array. Falls back to the
  // template values when config isn't loaded yet so we don't render
  // an empty strip on first paint.
  const chips: ChipDefinition[] = useMemo(() => {
    const fallback: ChipDefinition[] = CHIP_STYLES.map((style, i) => ({
      ...style,
      value: [0.1, 0.5, 1, 5, 10, 25, 50][i] ?? 1
    }))
    if (!isKnownGameConfig(gameConfig)) return fallback
    const currencyCode = gameConfig.bet.currency
    // gameConfig limits arrive as raw integer units → fromInteger.
    // `player.balance.amount` is ALREADY in decimal form (the player
    // store stores the converted value), so it must NOT be re-converted
    // — doing that would produce a near-zero balance and force the
    // smart-chip generator into the single-chip fallback path.
    const minBetDecimal = fromInteger(gameConfig.bet.minBetAmount, currencyCode)
    const maxBetDecimal = fromInteger(gameConfig.bet.maxBetAmount, currencyCode)
    const initialBalance = initialBalanceRef.current ?? 0
    if (minBetDecimal <= 0 || maxBetDecimal <= 0) return fallback
    // Use the operator's custom multipliers if set, otherwise let
    // `generateSmartChips` pick clean round values that fit the
    // player's balance + bet limits. `targetCount: 7` matches the
    // chip-bar layout designed around 7 chips (vs `BetInput`'s 5
    // default — that one is a horizontal toolbar, this is a wider
    // chip-strip with paginated mobile view).
    const targetChipCount = 7
    const rawValues = hasCustomChipMultipliers(gameConfig.bet.chipMultipliers ?? [])
      ? generateChipsFromMultipliers(gameConfig.bet.chipMultipliers ?? [], minBetDecimal)
      : generateSmartChips({
          minBet: minBetDecimal,
          maxBet: maxBetDecimal,
          balance: initialBalance,
          targetCount: targetChipCount
        })
    // Filter out anything below min or above max — the backend rejects
    // either way (`BetAmountOutOfRange`). Also drop chips above the
    // initial balance so the player can't pick a chip they couldn't
    // afford from the start.
    const allFiltered = rawValues.filter(
      (v) => v >= minBetDecimal && v <= maxBetDecimal && v <= initialBalance
    )
    // Custom-multiplier configs tend to ship 11 values
    // (`DEFAULT_CHIP_MULTIPLIERS`) so post-filter we can still have
    // more than `targetChipCount`. Down-sample evenly across the
    // range — keeps the smallest + largest visible, with even steps
    // in between — so the player sees a balanced spread instead of
    // either too few or too many chips.
    let filtered = allFiltered
    if (allFiltered.length > targetChipCount) {
      const step = (allFiltered.length - 1) / (targetChipCount - 1)
      filtered = []
      for (let i = 0; i < targetChipCount; i++) {
        const idx = Math.round(i * step)
        const value = allFiltered[idx]
        if (value !== undefined && !filtered.includes(value)) filtered.push(value)
      }
    }
    if (filtered.length === 0) {
      // Edge case: balance below min bet → still show min so the
      // player sees something rather than an empty strip. The
      // per-click guard will block placement anyway.
      return [{ value: minBetDecimal, ...CHIP_STYLES[0]! }]
    }
    // When the min bet sits below the smallest clean chip (e.g. a 0.75 min
    // bet whose smallest chip is 1), offer the exact min as an extra leading
    // chip so players can still place the minimum (QA-60081). The extra min
    // chip takes the muted grey (CHIP_STYLES[0]); the first real chip takes
    // the teal accent it freed up, and the rest keep their index-based colours
    // (so the largest chip stays purple).
    const withMinBet = prependMinBetChip(filtered, minBetDecimal)
    const minPrepended = withMinBet.length !== filtered.length
    return withMinBet.map((value, i) => {
      if (minPrepended && i === 0) return { value, ...CHIP_STYLES[0]! }
      const niceIdx = minPrepended ? i - 1 : i
      if (minPrepended && niceIdx === 0) return { value, ...MIN_BET_CHIP_STYLE }
      return { value, ...CHIP_STYLES[niceIdx % CHIP_STYLES.length]! }
    })
  }, [gameConfig, player.balance.amount])

  // Clamp the selected index to the live chip list — config changes
  // (or first load when fallback → real chips swaps the array length)
  // can leave selectedChipIdx pointing past the end.
  const safeChipIdx = Math.min(selectedChipIdx, chips.length - 1)
  const selectedChip = chips[safeChipIdx] ?? chips[0]!
  const selectedChipValue = selectedChip.value

  const markets = useMemo(() => {
    if (isKnownGameConfig(gameConfig) && gameConfig.markets?.length) {
      return enrichMarkets(gameConfig.markets, game)
    }
    // Fallback: single main market from game marbles (for games without market config yet)
    return [
      {
        id: 'main',
        type: 'main' as const,
        label: 'Race Winner',
        probabilities: {},
        limits: { PickWinner: { min: 0, max: 0 } },
        maxMultiplier: 0,
        displayOrder: 0,
        options: game.marbles
          .filter((m) => m.active)
          .map((m) => ({
            id: m.uuid,
            name: m.color,
            color: m.color.toLowerCase(),
            image: m.image || undefined,
            multiplier: 2.0,
            bettable: true
          }))
      }
    ] satisfies MarketConfig[]
  }, [gameConfig, game])

  // First market with type 'main' (or just the first market) drives the
  // forecast/tricast side-bets. The side-bet derivation only cares about
  // the main market's option IDs because forecast/tricast bets compose
  // marbles from the race winner pool.
  const mainMarket = useMemo(() => {
    return markets.find((m) => m.type === 'main') ?? markets[0]
  }, [markets])

  // Flatten every configured side-bet market's bettable options into
  // a single rail of compact pills shown below the main grid. Mirrors
  // the visual rhythm of the derived forecast/tricast `SideBetRows` so
  // the panel reads as one coherent side-bet rail.  Hidden options
  // (e.g. the "Neither" bucket on Exact-Boundary that exists only to
  // make the prob config sum to 100%) are filtered out via `bettable`.
  const configuredSideBetOptions = useMemo(() => {
    const out: { market: MarketConfig; option: MarketConfig['options'][number] }[] = []
    for (const market of markets) {
      if (market.type !== 'side_bet') continue
      for (const option of market.options) {
        if (!option.bettable) continue
        out.push({ market, option })
      }
    }
    return out
  }, [markets])
  const mainMarketOptionIds = useMemo(() => {
    return new Set(mainMarket?.options.map((o) => o.id) ?? [])
  }, [mainMarket])

  // Number of active marbles in the race. Used by the side-bet
  // multiplier formula and passed to `<SideBetRows>` — memoising it
  // keeps the prop reference (and the SideBetRows `rows` useMemo
  // downstream) stable across panel re-renders that don't change the
  // marble pool, e.g. chip placement, lightning reveal, settlement.
  // Pricing key for this game (from its race-configuration id), so
  // side-bet odds use its own margins rather than a flat constant.
  const sideBetGameKey = useMemo(() => resolveGameKey(game.id), [game.id])

  const totalActiveMarbles = useMemo(
    () => game.marbles.filter((m) => m.active).length,
    [game.marbles]
  )

  // Track the order in which the player has placed chips on distinct
  // main-market marbles. Drives `<SideBetRows />` — 2 marbles → forecast
  // rows, 3+ → tricast rows on top of forecast. Side-bet chip placements
  // never modify this list, so the rows stay stable while the player
  // adds chips to the derived forecast/tricast options.
  const [marbleOrder, setMarbleOrder] = useState<string[]>([])
  useEffect(() => {
    setMarbleOrder((prev) => {
      const stillPlaced = prev.filter((id) => placedChips[id] && mainMarketOptionIds.has(id))
      const placedMainIds = Object.keys(placedChips).filter((id) => mainMarketOptionIds.has(id))
      const stillSet = new Set(stillPlaced)
      const newcomers = placedMainIds.filter((id) => !stillSet.has(id))
      if (newcomers.length === 0 && stillPlaced.length === prev.length) return prev
      return [...stillPlaced, ...newcomers]
    })
  }, [placedChips, mainMarketOptionIds])

  // Restore chips from accepted bets (after page refresh). Handles
  // both single-selection main-market bets and multi-selection
  // forecast/tricast side-bets — without this side-bet tiles would
  // be empty after refresh until the next round opened.
  useEffect(() => {
    if (currentBets.length > 0 && Object.keys(placedChips).length === 0 && !betsSubmitted) {
      const restored: Record<string, PlacedChip> = {}
      // Forecast/tricast multipliers come from the shared module, which
      // holds each game's own margins (see `SideBetRows`). Replays
      // against the static marble count so the snapshot matches what
      // was originally placed.
      const computeSideBetMultiplier = (type: string, n: number): number => {
        switch (type) {
          case 'StraightForecast':
          case 'ReverseForecast':
          case 'Tricast':
          case 'CombinationTricast':
            return sideBetMultiplier(sideBetGameKey, type, n)
          default:
            return 0
        }
      }
      const buildSideBetLabel = (ids: string[], variant: 'exact' | 'any') => {
        const sep = variant === 'exact' ? ' → ' : ' + '
        return ids
          .map((id) => {
            const m = game.marbles.find((mm) => mm.uuid === id)
            if (!m) return '?'
            return m.color.charAt(0).toUpperCase() + m.color.slice(1).toLowerCase()
          })
          .join(sep)
      }
      const mainMarketID = mainMarket?.id ?? markets[0]?.id ?? 'main'
      // Replay bets in their original placement order. Side-bet chips
      // are keyed by `sidebet-{type}-{m1}-{m2}[-m3]` where the marble
      // IDs come from `marbleOrder`, which is itself derived from the
      // insertion order of main-market chips into `placedChips`. If
      // `currentBets` arrives in a different order than the player
      // clicked them, marbleOrder ends up shuffled and the side-bet
      // row's derived optionId no longer matches the restored chip's
      // key — leaving the chip in placedChips but no row to render
      // it on. Sorting by `createdAt` recovers the original click
      // order so the synthetic IDs realign.
      const orderedBets = [...currentBets].sort((a, b) => {
        const ta = a.createdAt ?? ''
        const tb = b.createdAt ?? ''
        return ta < tb ? -1 : ta > tb ? 1 : 0
      })
      for (const bet of orderedBets) {
        const items = bet.betDetails?.selectedItems
        const type = bet.betDetails?.type
        if (!items || items.length === 0 || !type) continue

        if (type === 'PickWinner' && items.length === 1) {
          const optionId = items[0]!
          const market = markets.find((m) => m.options.some((o) => o.id === optionId))
          if (!market) continue
          restored[optionId] = {
            optionId,
            marketId: market.id,
            amount: bet.betAmount,
            // Restoring from already-accepted bets after a refresh —
            // the per-click count was lost with the page state, so
            // collapse the stack to a single chip rather than try to
            // reverse-engineer it from the amount.
            count: 1
          }
          continue
        }

        if (
          (type === 'StraightForecast' ||
            type === 'ReverseForecast' ||
            type === 'Tricast' ||
            type === 'CombinationTricast') &&
          items.length >= 2
        ) {
          const variant: 'exact' | 'any' =
            type === 'StraightForecast' || type === 'Tricast' ? 'exact' : 'any'
          const optionId = `sidebet-${type}-${items.join('-')}`
          restored[optionId] = {
            optionId,
            marketId: mainMarketID,
            amount: bet.betAmount,
            count: 1,
            betType: type,
            selectedItems: [...items],
            multiplier: computeSideBetMultiplier(type, totalActiveMarbles),
            label: buildSideBetLabel(items, variant)
          }
        }
      }
      if (Object.keys(restored).length > 0) {
        restoredRoundIdRef.current = currentBets[0]?.raceId
        setPlacedChips(restored)
        setBetsSubmitted(true)
        // Mark the round as having a successful submission so the next
        // `race_ready` clears these chips. Without this, the race_ready
        // handler reads `betsActuallySent === false` and takes the
        // "keep chips for next round" branch (intended for unfinished
        // placements, not restored prior-round bets), and the player
        // walks into round N+1 with round N's bet still placed —
        // potentially auto-rebetting something they didn't want.
        setBetsActuallySent(true)
      }
    }
  }, [currentBets, markets, mainMarket, game.marbles, totalActiveMarbles, sideBetGameKey])

  // Lightning round data — gated for chip-placement so boosts only
  // appear AFTER betting closes (Crazy Time / Evolution-style flow).
  // The backend Round.LightningRevealedAt field is the authoritative
  // gate; the realtime / socket-server tier is responsible for stripping
  // lightning fields out of the race-state broadcast for chip-placement
  // clients pre-reveal so a sniffer can't see them.
  //
  // We additionally gate client-side based on the race phase:
  // `race_started` and beyond means betting closed → reveal allowed.
  // This is defense-in-depth (covers the window between deploying this
  // frontend and the backend gate landing) and gives us a clean signal
  // for the reveal animation moment.
  const currentRace = game.races[0]
  const rawIsLightningRound = currentRace?.isLightningRound ?? false
  const rawLightningBoostedItems = currentRace?.lightningBoostedItems ?? []
  const rawLightningBoostPercent = currentRace?.lightningBoostPercent ?? 0
  const lightningRevealAllowed =
    latestEvent?.eventType === 'race_started' ||
    latestEvent?.eventType === 'race_ended' ||
    latestEvent?.eventType === 'race_settled' ||
    latestEvent?.eventType === 'race_crashed'
  const isLightningRound = rawIsLightningRound && lightningRevealAllowed
  const lightningBoostedItems = useMemo<Set<string>>(
    () => new Set(lightningRevealAllowed ? rawLightningBoostedItems : []),
    [lightningRevealAllowed, rawLightningBoostedItems]
  )
  const lightningBoostPercent = lightningRevealAllowed ? rawLightningBoostPercent : 0

  // Lightning boost applies only to chips that were submitted FOR the
  // round whose lightning is currently revealed. Chips placed during
  // the race phase but not yet submitted (`!betsSubmitted` while
  // `lightningRevealAllowed`) are queued for the NEXT round — the
  // current round's lightning has nothing to do with them, so showing
  // the boosted multiplier or applying the boost to potential win
  // calculations would be misleading.
  const lightningBoostApplies = isLightningRound && betsSubmitted

  // Level-system multiplier bonus. Backend applies it unconditionally
  // when LevelSystemEnabled is true on the game (`register_bet.go`
  // and `calculate_payout.go` both gate on the flag, no extra
  // chip-placement gate). To keep the displayed multipliers and the
  // potential-win number honest about what the player will actually
  // receive, we factor the same bonus into the per-option pays display
  // and into `totalPotentialWin`.
  //
  // `playerLevel.multiplierBonus` is in PERCENTAGE POINTS (e.g. `0.4`
  // = +0.4%, level 4). Backend matches this in
  // `calculation.go::ApplyAllBoostsWithStreakAndLevel`:
  //   `boostFactor := 1 + (levelBoostPercent / 100)`
  // and `ChooseMarblePane.tsx` (classic mode) does the same — sums
  // `playerLevel.multiplierBonus` into a total then divides by 100.
  // Earlier this hook treated the value as a fraction-added-to-1
  // (0.4 = +40%) which made a 50x option display as 70x while the
  // bet itself paid 50.2x — divergent display vs payout. Dividing by
  // 100 here brings the displayed multiplier back in line with the
  // actual payout.
  // Re-renders automatically when the player levels up between rounds
  // (the `player_level_updated` channel handler updates the store →
  // selectors fire → this hook returns the new value).
  const isLevelSystemEnabled = useIsLevelSystemEnabled()
  const playerLevel = useGetPlayerLevel()
  const levelBoostFactor =
    isLevelSystemEnabled && playerLevel?.multiplierBonus ? 1 + playerLevel.multiplierBonus / 100 : 1

  // Reveal-moment timestamp — flips from null to Date.now() the first
  // time `lightningRevealAllowed` is true on a lightning round, then
  // back to null after the animation window so the same flag re-arms
  // for the next round. Drives the one-shot lightning-strike animation
  // on each boosted option + the corner badge entry.
  const [lightningRevealedAt, setLightningRevealedAt] = useState<number | null>(null)
  const lightningStrikeFiredRef = useRef(false)
  useEffect(() => {
    // Fire the one-shot strike only once the reveal is allowed AND the boosted
    // marbles have actually arrived. They now come via the gated
    // `lightning:revealed` socket event (~3s after betting closes, past the bet
    // grace), not on the race-state at race_started — so firing on the bare
    // reveal-allowed transition would flash an empty strike (no options to light
    // up) before the list lands. Gating on the data being present makes the
    // strike coincide with the boosted options.
    const revealedWithData =
      lightningRevealAllowed && rawIsLightningRound && rawLightningBoostedItems.length > 0
    if (!lightningStrikeFiredRef.current && revealedWithData) {
      lightningStrikeFiredRef.current = true
      setLightningRevealedAt(Date.now())
      // Thunder/strike sound layered on the existing lightningRound
      // effect, so the audio leads the visual flash by a beat.
      playEffect('lightningRound')
    }
  }, [lightningRevealAllowed, rawIsLightningRound, rawLightningBoostedItems, playEffect])

  // Auto-clear the reveal flag once the strike animation has finished
  // so the same option doesn't keep its `--revealing` class forever.
  useEffect(() => {
    if (lightningRevealedAt === null) return
    const id = setTimeout(() => setLightningRevealedAt(null), 1500)
    return () => clearTimeout(id)
  }, [lightningRevealedAt])
  const isLightningRevealing = lightningRevealedAt !== null

  // Round transition reset: when the next round opens (race_ready),
  // clear the reveal-moment flag so the next lightning round can fire
  // its own animation. Without this, a stale revealedAt from a prior
  // round's settle could carry into the next round's betting phase.
  useEffect(() => {
    if (latestEvent?.eventType === 'race_ready') {
      setLightningRevealedAt(null)
      lightningStrikeFiredRef.current = false
    }
  }, [latestEvent?.eventType])

  // Settlement state — drives the per-option win/loss visuals. We surface
  // wins inline on the option board (gold "WINNER" tag + payout pill on
  // the player's chip) instead of an overlay banner, so multi-bet rounds
  // (chip-placement / side-bets) all show their outcomes naturally in
  // place. `winningOptionIds` is a Set so per-option lookup is O(1) inside
  // the render loop. Once side-market outcomes propagate on the race
  // event we'll union them in here too — for now main-market only.
  const settledEvent = useMemo(
    () => currentRace?.raceEvents.find(isRaceSettledEvent),
    [currentRace?.raceEvents]
  )
  const isSettled = !!settledEvent

  // CCTV-only: pull the per-option boundary labels off the
  // race_started payload (the betting-closed transition). The
  // engine (irl-vehicle-counter) attaches `optionLabels`
  // (marbleId → "< 12" / "12 – 14" / "> 14" / "= 12 or = 14")
  // here, broadcast via Supabase realtime. Frozen for the round.
  // race_started rather than race_starting so we don't reveal
  // the bounds during the closing-countdown betting window.
  // Returns an empty map for non-CCTV games or pre-deploy CCTV
  // rounds (no payload), so callers can read it unconditionally.
  const cctvOptionLabels = useMemo<Record<string, string>>(() => {
    if (!isCCTVGameType(game.track.gameType)) return {}
    const startedEvent = currentRace?.raceEvents.find(isRaceStartedEvent)
    return startedEvent?.payload?.optionLabels ?? {}
  }, [currentRace?.raceEvents, game.track.gameType])
  // Boundary label only renders once betting has actually closed.
  // race_started is the trigger event; we keep the label on through
  // race_ended / race_settled / race_crashed so the player can read
  // the boundary alongside the result. Hidden during race_ready
  // and race_starting (still within betting).
  //
  // Gated on `betsSubmitted` — if the player skipped this round, the
  // chip-placement panel is showing the queue for the NEXT round and
  // these boundaries belong to the round currently in progress; surfacing
  // them would read as "the next round's ranges" and mislead. Mirrors
  // the same gate `lightningBoostApplies` uses for the same reason.
  const showCCTVBoundaries =
    betsSubmitted &&
    Object.keys(cctvOptionLabels).length > 0 &&
    (latestEvent?.eventType === 'race_started' ||
      latestEvent?.eventType === 'race_ended' ||
      latestEvent?.eventType === 'race_settled' ||
      latestEvent?.eventType === 'race_crashed')
  // Only position 1 is the actual "winner" for visual purposes (gold halo,
  // WinPill, payout call-out). Positions 2 and 3 still appear in the
  // settlement payload (the race engine submits podium results) but aren't
  // winning bets — players who staked them lost. Keeping them visible via
  // a small "2ND" / "3RD" tag below preserves race context without making
  // the option look like it won.
  const winningOptionIds = useMemo<Set<string>>(() => {
    if (!settledEvent) return new Set()
    return new Set(
      settledEvent.payload.results.filter((r) => r.position === 1).map((r) => r.marbleId)
    )
  }, [settledEvent])
  // optionId → finishing position (1, 2, or 3). Used to render the small
  // place-tag on non-winning podium options. Only positions 2 and 3 are
  // visually surfaced — position 1 is handled by the full WinPill.
  const placementByOptionId = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>()
    if (!settledEvent) return m
    for (const r of settledEvent.payload.results) {
      if (r.position >= 1 && r.position <= 3) m.set(r.marbleId, r.position)
    }
    return m
  }, [settledEvent])
  // Map each main-market option that has one of the player's
  // single-selection PickWinner bets on it to that bet, so the option
  // render can pick out status (won/lost) and payout in O(1).
  // Multi-selection bets (forecast/tricast) are intentionally excluded
  // here — they have their own keying scheme via `mySideBetByOptionId`
  // below — otherwise a forecast on [Yellow, Black] would clobber the
  // main Yellow PickWinner bet under key "Yellow" and the WinPill would
  // show the forecast's payout on the wrong tile.
  const myBetByOptionId = useMemo<Map<string, Bet>>(() => {
    const m = new Map<string, Bet>()
    for (const bet of currentBets) {
      const items = bet.betDetails?.selectedItems
      if (!items || items.length !== 1) continue
      if (bet.betDetails?.type !== 'PickWinner') continue
      m.set(items[0]!, bet)
    }
    return m
  }, [currentBets])

  // Same idea for side-bet (forecast/tricast) chips. The synthetic
  // optionId on the chip is `sidebet-{BetType}-{m1}-{m2}[-m3]` —
  // re-derive it from each multi-selection bet so the SideBetRows
  // render can look up its bet (status / payout) in O(1).
  const mySideBetByOptionId = useMemo<Map<string, Bet>>(() => {
    const m = new Map<string, Bet>()
    for (const bet of currentBets) {
      const type = bet.betDetails?.type
      const items = bet.betDetails?.selectedItems
      if (!items || items.length < 2) continue
      if (
        type !== 'StraightForecast' &&
        type !== 'ReverseForecast' &&
        type !== 'Tricast' &&
        type !== 'CombinationTricast'
      ) {
        continue
      }
      m.set(`sidebet-${type}-${items.join('-')}`, bet)
    }
    return m
  }, [currentBets])

  // Visible-option index map (id → its position across all bettable
  // options, in render order). Drives the staggered settle animation:
  // each `WinPill` delays its entry by `index * 80ms` so the row
  // cascades in instead of all firing in lockstep.
  const optionIndexById = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>()
    let i = 0
    for (const market of markets) {
      for (const option of market.options) {
        if (!option.bettable) continue
        m.set(option.id, i++)
      }
    }
    return m
  }, [markets])

  // Total wins for the settled round + the delay before the
  // "TOTAL WIN" banner should land. We delay relative to the LAST
  // winning pill so the banner reads as the capstone — pops in once
  // every per-option pill has finished its number count-up.
  const STAGGER_MS = 80
  const PILL_COUNTUP_TAIL_MS = 950 // entry pop + 220 delay + 700 count-up + slack
  const winningPlayerBets = useMemo<Bet[]>(() => {
    if (!isSettled) return []
    return currentBets.filter((b) => b.status === 'won')
  }, [isSettled, currentBets])
  const totalWinAmount = useMemo(
    () => winningPlayerBets.reduce((sum, b) => sum + b.potentialPayout, 0),
    [winningPlayerBets]
  )
  const totalWinDelayMs = useMemo(() => {
    let lastWinningIndex = -1
    for (const bet of winningPlayerBets) {
      const optionId = bet.betDetails?.selectedItems?.[0]
      if (!optionId) continue
      const idx = optionIndexById.get(optionId)
      if (idx !== undefined && idx > lastWinningIndex) lastWinningIndex = idx
    }
    if (lastWinningIndex < 0) return PILL_COUNTUP_TAIL_MS
    return lastWinningIndex * STAGGER_MS + PILL_COUNTUP_TAIL_MS
  }, [winningPlayerBets, optionIndexById])

  // Win tier — derived from the highest payout/stake ratio across the
  // player's winning bets. Drives both the size/glow of the per-option
  // win pill AND the layered sound effect (extra `bonus` chimes). The
  // thresholds are intentionally conservative: 10x for "big", 25x for
  // "mega", so the upgrade reads as earned rather than common.
  // (`tierForBet` is hoisted to module scope above.)
  const topWinTier = useMemo<WinTier>(() => {
    let best: WinTier = 'win'
    for (const bet of winningPlayerBets) {
      const t = tierForBet(bet)
      if (t === 'mega') return 'mega'
      if (t === 'big') best = 'big'
    }
    return best
  }, [winningPlayerBets])

  // Race-live state — between race_started and race_settled. Drives
  // the subtle "live bet" pulse on the marble image of any option the
  // player has a chip on, so during the race their stakes feel alive.
  const isRaceLive =
    latestEvent?.eventType === 'race_started' || latestEvent?.eventType === 'race_ended'

  // Collapse the betting board down to just the player's placed-bet tiles
  // during a live race on mobile. Frees vertical space for the livestream
  // (CCTV games on small phones were getting unwatchable). Gated on
  // `betsSubmitted` so chips queued for the NEXT round (betsSubmitted=false,
  // see line 220) don't trigger the collapse — those need the full grid
  // so the player can keep adding selections. Desktop and tablet untouched.
  const collapseToPlacedBets = isMobile && isRaceLive && betsSubmitted

  // Inventory auto-apply boosters. Sorted oldest-first so the first
  // booster the player earned is the first one consumed. As the player
  // places chips on options, each new option pulls the next unused
  // booster from this list and "locks" it in (see `appliedBoosters` /
  // `handleOptionClick` below). The booster boost is reflected in the
  // option's displayed multiplier and in the total potential win, but
  // ONLY for the option it's actually been assigned to — matching how the
  // backend will consume the boost on the corresponding RegisterBet.
  const inventoryItems = useInventoryItems()
  const availableBoosters = useMemo<Booster[]>(() => {
    return inventoryItems
      .filter(
        (item): item is Booster =>
          isBooster(item) &&
          // Only `claimed` items are usable. Once a booster has been
          // bound to a submitted bet the backend transitions it to
          // `applied` (still in the store, waiting for the bet to
          // settle to `used`). Without this status check the panel
          // would re-bind an `applied`/`used`/`expired`/`cancelled`
          // booster to a new chip, and the backend's
          // ValidateInventoryItemsForBet would reject it with
          // `InvalidInventoryItemError: item must have status
          // 'claimed', got 'applied'`. Surfaced via Datadog issue
          // 025a37b8-… in production.
          item.status === 'claimed' &&
          item.activationType === 'auto' &&
          item.boost.unit === 'percentage' &&
          (item.applicableGames.length === 0 || item.applicableGames.includes(game.id))
      )
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  }, [inventoryItems, game.id])

  // optionId → { boosterId, percent } for boosters already attached to a
  // placed chip. When the chip is removed the booster is freed back into
  // `availableBoosters` (next placement claims it again).
  const [appliedBoosters, setAppliedBoosters] = useState<
    Record<string, { boosterId: string; percent: number }>
  >({})

  const currency = player.balance.currency
  const currencySymbol = getCurrencySymbol(currency)
  const balance = player.balance.amount

  // Auto-apply free bets (Bonus type from inventory). Same shape as the
  // booster auto-apply: oldest-first FIFO, claimed on the first chip click
  // for an option. The option then locks to the free-bet amount — no
  // additional chips, no double, no manual stake on top. Each free bet is
  // single-use; once attached to an option for the round, it's consumed
  // when the bet is submitted.
  //
  // Eligibility filter:
  //   - Currency must match the player's wallet (sanity; backend would
  //     reject otherwise).
  //   - `applicableGames` must be empty (any game) or include this game.
  //   - Free-bet amount must fit between minBet and maxBet — otherwise the
  //     bet would be rejected at submit time. Silently skip rather than
  //     surfacing an error the player can't act on.
  const minBetForFilter = useMemo(
    () => (isKnownGameConfig(gameConfig) ? fromInteger(gameConfig.bet.minBetAmount, currency) : 0),
    [gameConfig, currency]
  )
  const maxBetForFilter = useMemo(
    () =>
      isKnownGameConfig(gameConfig) && gameConfig.bet.maxBetAmount > 0
        ? fromInteger(gameConfig.bet.maxBetAmount, currency)
        : Number.POSITIVE_INFINITY,
    [gameConfig, currency]
  )
  const availableFreeBets = useMemo<Bonus[]>(() => {
    return inventoryItems
      .filter(
        (item): item is Bonus =>
          isBonus(item) &&
          // Same status guard as availableBoosters — only `claimed`
          // free bets are usable; `applied`/`used`/`expired` get
          // rejected by the backend's ValidateInventoryItemsForBet.
          item.status === 'claimed' &&
          item.currency === currency &&
          item.amount >= minBetForFilter &&
          item.amount <= maxBetForFilter &&
          (item.applicableGames.length === 0 || item.applicableGames.includes(game.id))
      )
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  }, [inventoryItems, game.id, currency, minBetForFilter, maxBetForFilter])

  // optionId → { freeBetId, amount } for free bets attached to a placed
  // chip. The option's stake is locked to this amount (overriding the
  // selected chip value) until the chip is cleared, at which point the
  // free bet is freed back into `availableFreeBets`.
  const [appliedFreeBets, setAppliedFreeBets] = useState<
    Record<string, { freeBetId: string; amount: number }>
  >({})

  const totalStaked = useMemo(
    () => Object.values(placedChips).reduce((sum, c) => sum + c.amount, 0),
    [placedChips]
  )

  // Per-option max stake. Each placed chip on the board is its own bet,
  // so each option is independently capped by:
  //   - the global bet `maxBetAmount` (if set);
  //   - the global bet `limits.PickWinner.max` (if set, defensively read
  //     since chip-placement configs typically have top-level limits nil);
  //   - the global `bet.maxPayout / option.multiplier` so the bet can
  //     never resolve into a payout above maxPayout.
  //
  // The market-level `limits.PickWinner.max` is intentionally ignored:
  // the backend pre-computes it as `maxPayout / market.maxMultiplier`
  // (worst-case across all options), which over-restricts low-multiplier
  // options. Backend validation now uses the option's actual multiplier
  // (`register_bet.go::validateChipPlacementBetAmount`), so the
  // per-option `fromPayout` calc here is the matching frontend cap.
  // Limits stored as 0 mean "no limit" — we only enforce the ones the
  // game config actually sets.
  // Global per-bet caps (decimal units). Reused by `optionMaxBet` for
  // main-market options AND by `handleOptionClick` directly for
  // side-bet placements — side-bet rows have synthetic IDs that aren't
  // in `optionMaxBet`, so the click handler computes their cap on the
  // fly using the bet's snapshot multiplier and these globals. Without
  // this, side-bet stakes above `maxBetAmount` slipped through the
  // frontend and got rejected at the server with `BetAmountOutOfRange`.
  const globalBetCaps = useMemo(() => {
    if (!isKnownGameConfig(gameConfig)) {
      return {
        maxBetAmount: Number.POSITIVE_INFINITY,
        pickWinnerLimit: Number.POSITIVE_INFINITY,
        maxPayout: Number.POSITIVE_INFINITY
      }
    }
    const fromIntOrInf = (value: number) =>
      value > 0 ? fromInteger(value, currency) : Number.POSITIVE_INFINITY
    return {
      maxBetAmount: fromIntOrInf(gameConfig.bet.maxBetAmount),
      pickWinnerLimit: fromIntOrInf(gameConfig.bet?.limits?.PickWinner?.max ?? 0),
      maxPayout: fromIntOrInf(gameConfig.bet.maxPayout)
    }
  }, [gameConfig, currency])

  const optionMaxBet = useMemo(() => {
    const result = new Map<string, number>()
    if (!isKnownGameConfig(gameConfig)) return result
    const { maxBetAmount, pickWinnerLimit, maxPayout } = globalBetCaps

    for (const market of markets) {
      for (const option of market.options) {
        const fromPayout =
          option.multiplier > 0 ? maxPayout / option.multiplier : Number.POSITIVE_INFINITY
        result.set(option.id, Math.min(maxBetAmount, pickWinnerLimit, fromPayout))
      }
    }
    return result
  }, [gameConfig, markets, globalBetCaps])

  // Potential win = best possible outcome per market, summed across markets,
  // plus the sum of all side-bet chip payouts (independent bets, not
  // mutually exclusive with the main-market option win).
  const totalPotentialWin = useMemo(() => {
    // Split chips: market chips compete for "best in market"; side-bet
    // chips (carrying their own snapshotted multiplier) sum directly.
    const chipsByMarket = new Map<string, PlacedChip[]>()
    let sideBetTotal = 0
    for (const chip of Object.values(placedChips)) {
      if (chip.multiplier !== undefined) {
        // Side-bet chip: use the snapshotted multiplier and fold in
        // the same lightning + booster + level adjustments the
        // SideBetRows pill displays. Lightning applies if ANY of the
        // bet's selected marbles is in the boosted set (matches the
        // main option rule applied per-marble).
        let mult = chip.multiplier
        const isSideBetBoosted =
          lightningBoostApplies &&
          (chip.selectedItems ?? []).some((id) => lightningBoostedItems.has(id))
        if (isSideBetBoosted) {
          mult = mult * (1 + lightningBoostPercent / 100)
        }
        const optionBoosterPercent = appliedBoosters[chip.optionId]?.percent ?? 0
        if (optionBoosterPercent > 0) {
          mult = mult * (1 + optionBoosterPercent / 100)
        }
        mult = mult * levelBoostFactor
        sideBetTotal += chip.amount * mult
        continue
      }
      const existing = chipsByMarket.get(chip.marketId) ?? []
      existing.push(chip)
      chipsByMarket.set(chip.marketId, existing)
    }

    let totalBestWin = 0
    for (const [marketId, chips] of chipsByMarket) {
      const market = markets.find((m) => m.id === marketId)
      if (!market) continue

      // Find the best possible win for this market (highest single option payout)
      let bestMarketWin = 0
      for (const chip of chips) {
        const option = market.options.find((o) => o.id === chip.optionId)
        let mult = option?.multiplier ?? 0
        // Apply lightning boost only when the chips on the board are for
        // the round whose lightning is revealed (`lightningBoostApplies`).
        // Chips queued for the NEXT round are unaffected by the current
        // round's lightning data.
        if (lightningBoostApplies && lightningBoostedItems.has(chip.optionId)) {
          mult = mult * (1 + lightningBoostPercent / 100)
        }
        // Level-system multiplier bonus — applies to every bet uniformly
        // (backend computes the same factor in `register_bet.go`).
        // Factored in here so the displayed potential win matches the
        // actual payout the player will receive on settle.
        mult = mult * levelBoostFactor
        // Apply the inventory booster assigned to THIS option (each placed
        // option claims its own booster from the available pool, so the
        // payout reflects exactly what that bet would receive).
        const optionBoosterPercent = appliedBoosters[chip.optionId]?.percent ?? 0
        if (optionBoosterPercent > 0) {
          mult = mult * (1 + optionBoosterPercent / 100)
        }
        const payout = chip.amount * mult
        if (payout > bestMarketWin) {
          bestMarketWin = payout
        }
      }
      totalBestWin += bestMarketWin
    }
    return totalBestWin + sideBetTotal
  }, [
    placedChips,
    markets,
    lightningBoostApplies,
    lightningBoostedItems,
    lightningBoostPercent,
    appliedBoosters,
    levelBoostFactor
  ])

  useEffect(() => {
    onStatsChange?.({ totalStaked, totalPotentialWin, chipCount: Object.keys(placedChips).length })
  }, [totalStaked, totalPotentialWin, placedChips, onStatsChange])

  // Notice auto-clear. New messages reset the timer because they replace
  // `notice` immediately, which triggers this effect again. Each notice
  // carries its own duration so ambient context (CCTV disclaimer, ~3.5s)
  // and warning toasts (~2.5s) can co-exist with appropriate dwell.
  useEffect(() => {
    if (!notice) return
    const id = setTimeout(() => setNotice(null), notice.durationMs)
    return () => clearTimeout(id)
  }, [notice])

  // Clean up the receiving-pulse timer on unmount so a panel
  // remount mid-flight doesn't leak a setState into the void.
  useEffect(() => {
    return () => {
      if (receivingClearRef.current) clearTimeout(receivingClearRef.current)
    }
  }, [])

  // Big-win sound layering. Fires once per settled round, gated on
  // `topWinTier`. The base "you won" notification still plays from
  // `ShowRaceBannerContainer` (its sound logic runs even when the
  // banner is suppressed in chip-placement mode); we layer additional
  // `bonus` chimes here for big- and mega-tier wins. Timed to land
  // around when the per-option pill cascade is finishing so the
  // celebration sequence reads as one connected moment rather than
  // a sound and an animation that happen to overlap.
  useEffect(() => {
    if (!isSettled || totalWinAmount <= 0) return
    if (topWinTier === 'win') return
    const bonusDelay = totalWinDelayMs - 250 // arrives just before the banner
    const id1 = setTimeout(() => playEffect('bonus'), Math.max(0, bonusDelay))
    let id2: ReturnType<typeof setTimeout> | null = null
    if (topWinTier === 'mega') {
      id2 = setTimeout(() => playEffect('bonus'), Math.max(0, bonusDelay + 320))
    }
    return () => {
      clearTimeout(id1)
      if (id2) clearTimeout(id2)
    }
  }, [isSettled, totalWinAmount, topWinTier, totalWinDelayMs, playEffect])

  // Helper: whenever a refusal sound is played we also surface a banner
  // so the user understands WHY the action didn't take effect, mirroring
  // the default PlaceBetPanel which shows messages like "Max Payout
  // Reached" / "Insufficient Balance". Declared before `handleOptionClick`
  // / `handleDouble` / `handleRepeat` because those `useCallback`s list it
  // as a dependency, and TDZ would fire otherwise.
  const showNotice = useCallback(
    (
      message: string,
      tone: 'warning' | 'success' | 'info' = 'warning',
      durationMs: number = 2500
    ) => {
      // Force a state-change even when the same message fires twice in a
      // row, so the timeout and any future fade animation restart.
      setNotice(null)
      requestAnimationFrame(() => setNotice({ message, tone, durationMs }))
    },
    []
  )

  // Persistent CCTV disclaimer. Lives in its own pill slot above the
  // transient notice slot so both can be visible at once — when a
  // warning fires (e.g. "Max bet reached") it appears below the
  // disclaimer rather than replacing it. Lifecycle:
  //   betting open (race_ready, no race_started yet) → visible
  //   race_started fires                              → visible for 4s
  //   4s after race_started                           → hidden (until next round)
  // Falls back to non-CCTV / no-current-race / unknown game type
  // by keeping the slot empty.
  const cctvDisclaimerCopy = useMemo(() => {
    if (!isCCTVGameType(game.track.gameType)) return null
    return getCCTVCopy(game.track.gameType, t)?.disclaimer ?? null
  }, [game.track.gameType, t])

  const [showCctvDisclaimer, setShowCctvDisclaimer] = useState(false)
  useEffect(() => {
    if (!cctvDisclaimerCopy || !currentRace) {
      setShowCctvDisclaimer(false)
      return
    }
    const events = currentRace.raceEvents
    const hasReady = events.some((e) => e.eventType === 'race_ready')
    const startedEvent = events.find(isRaceStartedEvent)
    if (!hasReady) {
      // Round hasn't opened yet (or this race object is in a stub
      // pre-events state). Wait for it.
      setShowCctvDisclaimer(false)
      return
    }
    if (!startedEvent) {
      // Betting open — keep visible until race_started fires.
      setShowCctvDisclaimer(true)
      return
    }
    // race_started has fired — show until 4s post-start, then fade.
    const HIDE_AFTER_MS = 4000
    const startedAt = Date.parse(startedEvent.eventTimestamp)
    if (Number.isNaN(startedAt)) {
      // Malformed timestamp — degrade by hiding immediately.
      setShowCctvDisclaimer(false)
      return
    }
    const ageMs = Date.now() - startedAt
    if (ageMs >= HIDE_AFTER_MS) {
      setShowCctvDisclaimer(false)
      return
    }
    setShowCctvDisclaimer(true)
    const remainingMs = HIDE_AFTER_MS - ageMs
    const hideTimer = setTimeout(() => setShowCctvDisclaimer(false), remainingMs)
    return () => clearTimeout(hideTimer)
  }, [cctvDisclaimerCopy, currentRace])

  // Stop auto-bet whenever the player modifies the bet pattern (place
  // chip, double, undo, trash). Matches Evolution's "set the pattern,
  // let it run; touch anything to stop" model — easier to recover from
  // an unintended stop than from accidentally auto-betting a modified
  // pattern. Reads via ref so handlers don't get a fresh callback every
  // toggle.
  const disableAutoBetOnEdit = useCallback(() => {
    if (!autoBetRef.current) return
    setAutoBet(false)
    showNotice(t('Auto-bet stopped — bet modified'))
  }, [showNotice, t])

  // A chip laid on a hard round whose board has locked while the round is
  // still open is not for that round: it stays for the next one. Logged once a
  // round; set after the lock below (`useBetLock`), read through this ref.
  const noteChipAfterLockRef = useRef<() => void>(() => undefined)

  const handleOptionClick = useCallback(
    (
      marketId: string,
      optionId: string,
      sideBet?: {
        betType: BetType
        selectedItems: string[]
        multiplier: number
        label: string
      }
    ) => {
      noteChipAfterLockRef.current()
      // Free-bet-locked options can't take additional chips, doubles, or
      // top-ups. The free-bet amount IS the bet — anything else would
      // require splitting the bet, which the backend doesn't support.
      const freeBetOnOption = appliedFreeBets[optionId]
      if (freeBetOnOption) {
        playEffect('cancel')
        showNotice('Free bet placed — no additional stake on this option')
        return
      }

      const currentOnOption = placedChips[optionId]?.amount ?? 0
      const isFirstChipOnOption = currentOnOption === 0

      // First-click free-bet auto-claim (FIFO). If the player has an
      // unused free bet that fits this game's limits, use it instead of
      // the player-selected chip value — the option locks to the free-bet
      // amount, the free-bet's inventory item ID rides along on the
      // RegisterBet payload (see useChipPlacementBetSubmit), and the
      // backend consumes the free bet on register.
      //
      // Free bet trumps the chip-value-based stake the player clicked on:
      // even if they had a 100-chip selected, a $25 free bet wins. This
      // matches the user-confirmed UX from the chip-placement spec.
      if (isFirstChipOnOption) {
        const usedFreeBetIds = new Set(Object.values(appliedFreeBets).map((a) => a.freeBetId))
        const nextFreeBet = availableFreeBets.find((fb) => !usedFreeBetIds.has(fb.id))
        if (nextFreeBet) {
          // Free bets don't consume balance, so we skip the totalStake
          // balance check and the per-option-max check (free-bet amounts
          // are pre-filtered to fit minBet/maxBet, see availableFreeBets).
          const chipEl = chipSelectorRef.current?.querySelector('.chip-panel__chip--active')
          const optEl = optionRefs.current[optionId]
          if (chipEl && optEl) {
            const chipRect = chipEl.getBoundingClientRect()
            const optRect = optEl.getBoundingClientRect()
            const flyId = `fly-${Date.now()}`
            setFlyingChips((prev) => [
              ...prev,
              {
                id: flyId,
                fromX: chipRect.left + chipRect.width / 2,
                fromY: chipRect.top + chipRect.height / 2,
                toX: optRect.right - 20,
                toY: optRect.top + 20,
                bg: selectedChip.bg,
                border: selectedChip.border,
                amount: nextFreeBet.amount
              }
            ])
            setTimeout(() => {
              setFlyingChips((prev) => prev.filter((c) => c.id !== flyId))
            }, 350)
          }

          playEffect('chip')

          disableAutoBetOnEdit()
          undoHistoryRef.current.push({
            chips: { ...placedChips },
            boosters: { ...appliedBoosters },
            freeBets: { ...appliedFreeBets }
          })
          setAppliedFreeBets((prev) => ({
            ...prev,
            [optionId]: { freeBetId: nextFreeBet.id, amount: nextFreeBet.amount }
          }))
          setPlacedChips((prev) => ({
            ...prev,
            [optionId]: {
              optionId,
              marketId,
              amount: nextFreeBet.amount,
              count: 1
            }
          }))

          setReceivingOption(null)
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setReceivingOption(optionId))
          })
          if (receivingClearRef.current) clearTimeout(receivingClearRef.current)
          receivingClearRef.current = setTimeout(() => setReceivingOption(null), 800)
          return
        }
      }

      // Limit checks (per-option max bet + total stake ≤ balance). Each
      // placed chip becomes a separate bet at submit time, so the option's
      // total stake must fit under that option's per-bet cap; the cumulative
      // stake across options must fit under the player's balance.
      const newOnOption = currentOnOption + selectedChipValue
      const newTotalStake = totalStaked + selectedChipValue
      // Side-bet rows have synthetic optionIds (`sidebet-{Type}-...`)
      // that aren't in `optionMaxBet`. Compute their cap inline from
      // the snapshot multiplier so the same `maxBetAmount` /
      // `maxPayout / multiplier` rule applies — the backend's
      // `validateChipPlacementBetAmount` enforces the same cap server-
      // side, and we don't want `BetAmountOutOfRange` rejections (the
      // optimistic balance debit doesn't always cleanly roll back).
      const maxForOption = sideBet
        ? Math.min(
            globalBetCaps.maxBetAmount,
            sideBet.multiplier > 0
              ? globalBetCaps.maxPayout / sideBet.multiplier
              : Number.POSITIVE_INFINITY
          )
        : (optionMaxBet.get(optionId) ?? Number.POSITIVE_INFINITY)

      // Refuse + surface the reason. Per-option cap is checked first so
      // the more specific message wins when both would apply (e.g. user
      // has plenty of balance but the option's max bet is reached).
      if (newOnOption > maxForOption) {
        playEffect('cancel')
        showNotice('Max reached on this option')
        return
      }
      // Safety: never stake before the real balance has loaded. The non-blocking
      // launch renders the game before the operator-wallet balance arrives.
      if (!player.balanceLoaded) {
        playEffect('cancel')
        showNotice('Initializing balance')
        return
      }
      if (newTotalStake > balance) {
        playEffect('cancel')
        showNotice('Insufficient balance')
        emitBalanceInsufficient({
          requiredAmount: newTotalStake,
          currentBalance: balance,
          currency
        })
        return
      }

      // Get positions for flying animation
      const chipEl = chipSelectorRef.current?.querySelector('.chip-panel__chip--active')
      const optEl = optionRefs.current[optionId]

      if (chipEl && optEl) {
        const chipRect = chipEl.getBoundingClientRect()
        const optRect = optEl.getBoundingClientRect()
        const flyId = `fly-${Date.now()}`

        setFlyingChips((prev) => [
          ...prev,
          {
            id: flyId,
            fromX: chipRect.left + chipRect.width / 2,
            fromY: chipRect.top + chipRect.height / 2,
            toX: optRect.right - 20,
            toY: optRect.top + 20,
            bg: selectedChip.bg,
            border: selectedChip.border,
            amount: selectedChipValue
          }
        ])

        // Remove flying chip after animation
        setTimeout(() => {
          setFlyingChips((prev) => prev.filter((c) => c.id !== flyId))
        }, 350)
      }

      playEffect('chip')

      // Snapshot the pre-placement board so undo reverses just this one
      // chip (QA-60196), taken before the booster claim + chip mutation.
      undoHistoryRef.current.push({
        chips: { ...placedChips },
        boosters: { ...appliedBoosters },
        freeBets: { ...appliedFreeBets }
      })

      // First chip on this option claims the next unused auto-booster
      // (oldest first). Subsequent chips on the same option don't re-claim
      // — the booster stays attached to that option until its chips are
      // cleared.
      if (isFirstChipOnOption) {
        const usedBoosterIds = new Set(Object.values(appliedBoosters).map((a) => a.boosterId))
        const next = availableBoosters.find((b) => !usedBoosterIds.has(b.id))
        if (next) {
          setAppliedBoosters((prev) => ({
            ...prev,
            [optionId]: { boosterId: next.id, percent: next.boost.value }
          }))
        }
      }

      disableAutoBetOnEdit()
      setPlacedChips((prev) => {
        const existing = prev[optionId]
        const newAmount = (existing?.amount ?? 0) + selectedChipValue
        const newCount = (existing?.count ?? 0) + 1
        return {
          ...prev,
          [optionId]: {
            optionId,
            marketId,
            amount: newAmount,
            count: newCount,
            // Preserve the side-bet payload across stack-up clicks. The
            // first placement (existing undefined) seeds these via the
            // `sideBet` arg; later placements on the same option carry
            // the same payload through.
            ...(sideBet
              ? {
                  betType: sideBet.betType,
                  selectedItems: sideBet.selectedItems,
                  multiplier: sideBet.multiplier,
                  label: sideBet.label
                }
              : existing?.betType
                ? {
                    betType: existing.betType,
                    selectedItems: existing.selectedItems,
                    multiplier: existing.multiplier,
                    label: existing.label
                  }
                : {})
          }
        }
      })

      // Mark this option as "receiving" so it pulses warmly the
      // moment the flying chip lands on it (CSS animation-delay
      // syncs the pulse to the chip's arrival, not the click).
      // Reset-then-set across two RAFs so rapid taps on the same
      // option re-fire the animation each time.
      setReceivingOption(null)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setReceivingOption(optionId))
      })
      if (receivingClearRef.current) clearTimeout(receivingClearRef.current)
      receivingClearRef.current = setTimeout(() => setReceivingOption(null), 800)
    },
    [
      selectedChipValue,
      selectedChip,
      placedChips,
      totalStaked,
      balance,
      optionMaxBet,
      globalBetCaps,
      playEffect,
      showNotice,
      appliedBoosters,
      availableBoosters,
      appliedFreeBets,
      availableFreeBets,
      disableAutoBetOnEdit,
      currency
    ]
  )

  const handleRemoveChip = useCallback(
    (optionId: string) => {
      disableAutoBetOnEdit()
      setPlacedChips((prev) => {
        const removed = prev[optionId]
        const updated = { ...prev }
        delete updated[optionId]
        // If we just removed a main-market marble chip, sweep any
        // dependent side-bet chips (forecast/tricast that reference
        // this marble) so the player isn't left with hidden bets the
        // panel can no longer render. Side-bet chips themselves are
        // exempt — removing a forecast chip doesn't cascade.
        if (removed && removed.betType === undefined) {
          for (const [id, chip] of Object.entries(updated)) {
            if (chip.selectedItems?.includes(optionId)) {
              delete updated[id]
            }
          }
        }
        return updated
      })
      // Free the booster attached to this option so a future placement on
      // another option can claim it.
      setAppliedBoosters((prev) => {
        if (!(optionId in prev)) return prev
        const updated = { ...prev }
        delete updated[optionId]
        return updated
      })
      // Release the free bet attached to this option back into the pool.
      setAppliedFreeBets((prev) => {
        if (!(optionId in prev)) return prev
        const updated = { ...prev }
        delete updated[optionId]
        return updated
      })
    },
    [disableAutoBetOnEdit]
  )

  const handleClearAll = useCallback(() => {
    setPlacedChips({})
    setAppliedBoosters({})
    setAppliedFreeBets({})
  }, [])

  const handleUndo = useCallback(() => {
    // Pop the most recent snapshot and restore it — reverses exactly one
    // action. For a stacked option this peels off just the last chip
    // (restoring the prior amount) instead of removing the whole option
    // (QA-60196); boosters/free bets auto-attached by that action are
    // also reverted because the snapshot predates them.
    const snapshot = undoHistoryRef.current.pop()
    if (!snapshot) return
    disableAutoBetOnEdit()
    setPlacedChips(snapshot.chips)
    setAppliedBoosters(snapshot.boosters)
    setAppliedFreeBets(snapshot.freeBets)
  }, [disableAutoBetOnEdit])

  // Whenever the board empties — clear-all, round transition, or
  // undo-to-empty — drop the undo history so a later undo can't
  // resurrect chips from before a deliberate clear. Centralising it
  // here means we don't have to reset history at every clear site.
  useEffect(() => {
    if (Object.keys(placedChips).length === 0 && undoHistoryRef.current.length > 0) {
      undoHistoryRef.current = []
    }
  }, [placedChips])

  const handleDouble = useCallback(() => {
    // Compute the doubled state SYNCHRONOUSLY from the live chip ref
    // (not via `setPlacedChips((prev) => ...)`), because React 18
    // defers functional state-updaters until reconciliation — any
    // closure variables we'd mutate inside the updater are NOT
    // visible immediately after `setState()` returns. Doing the
    // calculation up-front lets the notice logic see the correct
    // hit-balance / no-growth flags every click. Same trade-off
    // each chip makes in the loop:
    //   target = min(amount * 2, per-option max)
    //   allowed growth ≤ remaining (balance - already-staked)
    //   when a chip saturates (or balance runs out), the next ×2 tap
    //   continues growing whichever chips can still grow.
    const prev = placedChipsRef.current
    if (Object.keys(prev).length === 0) return

    disableAutoBetOnEdit()
    // Snapshot pre-double board so undo reverts the whole ×2 in one step.
    undoHistoryRef.current.push({
      chips: { ...prev },
      boosters: { ...appliedBoostersRef.current },
      freeBets: { ...appliedFreeBetsRef.current }
    })

    const baseTotal = Object.values(prev).reduce((s, c) => s + c.amount, 0)
    let remaining = Math.max(0, balance - baseTotal)
    let anyGrowth = false
    let hitBalanceCap = false
    const result: Record<string, PlacedChip> = {}

    for (const [key, chip] of Object.entries(prev)) {
      // Free-bet options are locked to the free-bet amount — the bet
      // can't be doubled because the backend consumes a single inventory
      // item with a fixed value. Pass through unchanged.
      if (appliedFreeBetsRef.current[chip.optionId]) {
        result[key] = chip
        continue
      }

      const max = optionMaxBet.get(chip.optionId) ?? Number.POSITIVE_INFINITY
      const target = Math.min(chip.amount * 2, max)
      // Math.max(0, …) protects against edge case where chip.amount
      // is somehow already > max (game config could change between
      // rounds): we never shrink a chip via ×2, only grow or no-op.
      const wantsGrowth = Math.max(0, target - chip.amount)
      const allowedGrowth = Math.min(wantsGrowth, remaining)
      const newAmount = chip.amount + allowedGrowth
      result[key] = { ...chip, amount: newAmount }
      remaining -= allowedGrowth
      if (allowedGrowth > 0) anyGrowth = true
      if (allowedGrowth < wantsGrowth) hitBalanceCap = true
    }

    setPlacedChips(result)

    // Notice logic uses the computed flags — accurate for every click:
    //   hitBalanceCap: at least one chip's growth was clipped by the
    //     remaining balance budget. Player should know.
    //   !anyGrowth: nothing grew at all (every chip is already at its
    //     per-option max). The "max reached" notice is only correct
    //     in this case — chips growing AT ALL means the click did
    //     something useful, no notice needed even if some happened
    //     to land exactly at max.
    if (hitBalanceCap) {
      showNotice('Insufficient balance to fully double')
      emitBalanceInsufficient({
        requiredAmount: baseTotal * 2,
        currentBalance: balance,
        currency
      })
    } else if (!anyGrowth) {
      showNotice('Max reached on all options')
    }

    // Clear-then-set across two animation frames so the `--doubling`
    // class is genuinely removed before being re-added — that's what
    // makes the CSS keyframe animation restart on rapid consecutive
    // ×2 taps.
    setDoubleAnimAt(0)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setDoubleAnimAt(Date.now()))
    })
  }, [balance, optionMaxBet, showNotice, disableAutoBetOnEdit, currency])

  // Auto-clear the double animation flag once the keyframe finishes, so
  // the class can re-trigger cleanly on the next ×2 press.
  useEffect(() => {
    if (doubleAnimAt === 0) return
    const id = setTimeout(() => setDoubleAnimAt(0), 500)
    return () => clearTimeout(id)
  }, [doubleAnimAt])

  // Same pattern for the auto-bet icon spin: each toggle fires one full
  // 360° rotation, then the flag clears.
  useEffect(() => {
    if (autoSpinAt === 0) return
    const id = setTimeout(() => setAutoSpinAt(0), 600)
    return () => clearTimeout(id)
  }, [autoSpinAt])

  const handleRepeat = useCallback(() => {
    if (!lastBets) return
    noteChipAfterLockRef.current()

    // Sanitise the repeat against the current round's market state
    // before applying. Two things can change between when chips were
    // saved as `lastBets` and now:
    //   1. Options may no longer be bettable (market reshuffled, or
    //      the option was removed) — drop those chips silently.
    //   2. The per-option max may have shrunk — clamp each chip to
    //      the current cap so we never re-place a stake the option
    //      can't accept.
    // Failing to filter here means the next submit would partially
    // reject, leaving the player with a confusing mix of placed and
    // missing chips.
    const bettableOptionIds = new Set<string>()
    for (const market of markets) {
      for (const opt of market.options) {
        if (opt.bettable) bettableOptionIds.add(opt.id)
      }
    }

    const validBets: Record<string, PlacedChip> = {}
    let total = 0
    for (const [optionId, chip] of Object.entries(lastBets)) {
      // Side-bet chips: their `optionId` is synthetic (sidebet-...) so
      // it's never in `bettableOptionIds`. Validate by checking that
      // every marble in `selectedItems` is still bettable in the main
      // market. The chip carries its multiplier + label snapshot from
      // the original placement, so we just re-spread the chip and the
      // forecast/tricast payload comes along.
      if (chip.betType !== undefined) {
        const items = chip.selectedItems ?? []
        if (items.length === 0 || !items.every((id) => bettableOptionIds.has(id))) continue
        const cappedAmount = chip.amount
        if (cappedAmount <= 0) continue
        validBets[optionId] = { ...chip, amount: cappedAmount }
        total += cappedAmount
        continue
      }
      // Main-market chip
      if (!bettableOptionIds.has(optionId)) continue
      const max = optionMaxBet.get(optionId) ?? Number.POSITIVE_INFINITY
      const cappedAmount = Math.min(chip.amount, max)
      if (cappedAmount <= 0) continue
      validBets[optionId] = { ...chip, amount: cappedAmount }
      total += cappedAmount
    }

    if (Object.keys(validBets).length === 0) {
      playEffect('cancel')
      showNotice('No valid options to repeat')
      return
    }
    if (total > balance) {
      playEffect('cancel')
      showNotice('Insufficient balance to repeat')
      emitBalanceInsufficient({
        requiredAmount: total,
        currentBalance: balance,
        currency
      })
      return
    }

    // Snapshot pre-repeat board so undo reverts the repeat in one step.
    undoHistoryRef.current.push({
      chips: { ...placedChipsRef.current },
      boosters: { ...appliedBoostersRef.current },
      freeBets: { ...appliedFreeBetsRef.current }
    })
    setPlacedChips(validBets)
    // Re-assign boosters using the last round's booster→option
    // mapping when possible: if the same booster ID is still in the
    // pool (not yet consumed) AND the option is in the repeated bet
    // set, restore that pairing exactly. Falls back to oldest-first
    // for any options that weren't booster-assigned last round, or
    // whose booster has been consumed since.
    const optionIds = Object.keys(validBets)
    const reassigned: Record<string, { boosterId: string; percent: number }> = {}
    const usedBoosterIds = new Set<string>()
    for (const optionId of optionIds) {
      const previous = lastAppliedBoosters[optionId]
      if (!previous) continue
      const stillAvailable = availableBoosters.find((b) => b.id === previous.boosterId)
      if (!stillAvailable) continue
      reassigned[optionId] = { boosterId: previous.boosterId, percent: previous.percent }
      usedBoosterIds.add(previous.boosterId)
    }
    // Fill remaining options in order with the next available booster
    // not already taken by the snapshot — mirrors a fresh sequence of
    // clicks for any options the player added since the snapshot.
    const fallbackPool = availableBoosters.filter((b) => !usedBoosterIds.has(b.id))
    let fallbackIdx = 0
    for (const optionId of optionIds) {
      if (reassigned[optionId]) continue
      const booster = fallbackPool[fallbackIdx]
      if (!booster) break
      fallbackIdx++
      reassigned[optionId] = { boosterId: booster.id, percent: booster.boost.value }
    }
    setAppliedBoosters(reassigned)
  }, [
    lastBets,
    balance,
    markets,
    optionMaxBet,
    playEffect,
    showNotice,
    availableBoosters,
    lastAppliedBoosters,
    currency
  ])

  // Latest-state refs so async closures (setTimeout callbacks, the
  // submitAllBets continuation) always read the freshest values
  // regardless of when the closure was created.
  const placedChipsRef = useRef(placedChips)
  placedChipsRef.current = placedChips
  const appliedBoostersRef = useRef(appliedBoosters)
  appliedBoostersRef.current = appliedBoosters
  const appliedFreeBetsRef = useRef(appliedFreeBets)
  appliedFreeBetsRef.current = appliedFreeBets
  // Auto-bet via ref so the per-modifier helpers can short-circuit when
  // it's already off without re-creating the parent callbacks on every
  // toggle. Mirrors the Evolution-style "any edit turns auto-bet off"
  // pattern: chip click, double, undo, trash, etc. all stop the loop.
  const autoBetRef = useRef(autoBet)
  autoBetRef.current = autoBet
  const currentRoundIdRef = useRef(currentRace?.uuid)
  currentRoundIdRef.current = currentRace?.uuid
  // Balance via ref so the submission/race_ready effects can read
  // the latest value without re-subscribing on every balance change.
  // Putting `balance` directly in those deps caused an update loop:
  // the silent-failure cleanup refunds via `playerActions.betUnaccepted`
  // → balance changes → effect re-runs → schedules more state updates
  // → max-update-depth.
  const balanceRef = useRef(balance)
  balanceRef.current = balance
  // Fresh balance-loaded flag for the submit-time safety guards below.
  const balanceLoadedRef = useRef(player.balanceLoaded)
  balanceLoadedRef.current = player.balanceLoaded
  // Same ref pattern for `availableBoosters` — `useInventoryItems`
  // returns a fresh `state.items.filter(...)` array on every store
  // read, which makes the `useMemo` chain produce a new reference
  // each render. Putting it directly in the race_ready effect deps
  // caused the same max-update-depth pattern as `balance` did. We
  // only need the latest snapshot when the effect actually fires.
  const availableBoostersRef = useRef(availableBoosters)
  availableBoostersRef.current = availableBoosters

  // ── Submission lifecycle ─────────────────────────────────────────
  //
  // Goal: chips placed during betting → submitted exactly once when
  // betting closes → result reflected back on the chips.
  //
  // The model is roulette-table: chips live on the board across
  // rounds. They aren't cleared on `race_ready` — instead they
  // become the player's intent for the new round, get auto-submitted
  // when betting closes, and the cycle repeats. Player can clear
  // manually with the trash button or modify by clicking options.
  //
  // Single trigger path: `race_starting` → schedule timer → submit.
  // `race_started` is a safety fallback only (timer should always
  // fire first, but if the closing phase is shorter than the timer
  // delay we catch the window here).
  // (betsSubmitted, betsActuallySent, and the submission-state refs
  //  are declared near the top of the component instead of co-located
  //  with the auto-bet pipeline — they're consumed by `totalPotentialWin`
  //  and the per-option multiplier display, which need to know whether
  //  chips on the board belong to the current round (boost applies) or
  //  are queued for the next round (boost doesn't apply yet).)
  const submissionStartedRef = useRef(false)
  const submittedRoundIdRef = useRef<string | undefined>(undefined)
  const submittedBetIdsRef = useRef<string[]>([])
  // Read by the mounted-once bet-store watcher below, so a language change reaches it.
  const notInTimeTextRef = useRef('')
  notInTimeTextRef.current = t('toast.errors.notInTime')
  const submitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconcileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Round id for which the freeze gate (B1) already held a submission. The
  // race_started fallback checks this so it does NOT re-submit a round the
  // timer path deliberately held (the fallback has no armed timer to measure,
  // so it can't re-detect the freeze). Cleared on the next race_ready.
  const staleHeldRoundIdRef = useRef<string | undefined>(undefined)
  // Fresh mirror of `betsActuallySent` so the phase-keyed submission effect
  // (B3 latest-ref: it does NOT re-run on state change) can read whether the
  // PREVIOUS round actually placed a bet when recovering from a dropped
  // race_ready (B4) — needed to mirror the clearAll-vs-carryForward board
  // decision and avoid re-betting chips that were already bet.
  const betsActuallySentRef = useRef(betsActuallySent)
  betsActuallySentRef.current = betsActuallySent

  // The hard close (rev 4, DESIGN §6). On a round whose race_ready says
  // bet_close "hard" the board locks at the FIRST of race_starting's arrival
  // and its own countdown, empty or not, and sends every option at once from
  // `onHardLock` (below; read through a ref). It has no 1.5 s timer and no
  // race_started fallback, and a chip laid after the lock is for the next
  // round. Every other round: `betLock.hard` false, and the panel is today's.
  const onHardLockRef = useRef<(sent: BetLockSent) => void>(() => undefined)
  const betLock = useBetLock({
    race: currentRace,
    previousRace: game.races[1],
    onLock: (sent) => onHardLockRef.current(sent)
  })
  const hardLocked = betLock.hard && betLock.lockedBy !== undefined
  // The round the chip-after-lock line was written for (`noteChipAfterLockRef`).
  const chipAfterLockRoundRef = useRef<string | undefined>(undefined)
  noteChipAfterLockRef.current = () => {
    if (!betLock.hard) return
    chipAfterLockRoundRef.current = logChipAfterLockOnce({
      race: currentRace,
      previousRace: game.races[1],
      phase: latestEvent?.eventType,
      loggedRoundId: chipAfterLockRoundRef.current
    })
  }

  // Read by the race_ready reset below without making it re-run.
  const betLockHardRef = useRef(betLock.hard)
  betLockHardRef.current = betLock.hard
  // The bets this round's hard lock put on the wire, for stage 2's line.
  const [hardSentBetIds, setHardSentBetIds] = useState<string[]>([])
  const stageTwo = useStageTwo({
    lockedAt: betLock.lockedAt,
    lockLeadS: betLock.lockLeadS,
    betIds: hardSentBetIds
  })

  const isBettingOpen =
    (latestEvent?.eventType === 'race_ready' || latestEvent?.eventType === 'race_starting') &&
    !hardLocked

  // Per-option submission state — drives the small confirm/fail
  // animations on the chip after the bet hits the wire. Populated
  // by `triggerSubmission` on submit, then transitions per option:
  //   placing  → bet pushed, awaiting bet:accepted / bet:rejected
  //   confirmed → bet:accepted arrived (~400ms pulse, then idle)
  //   failed   → bet:rejected, timeout, or silent-failure (~400ms,
  //              then chip clears)
  // The `placing` state is internal-only (no visual) — it just lets
  // us know which bets we're tracking for resolution.
  type SubmissionState = 'placing' | 'confirmed' | 'failed'
  const [submissionStates, setSubmissionStates] = useState<Record<string, SubmissionState>>({})
  // Map of betId → optionId for the currently-tracked submission, so
  // we can resolve a bet:accepted / bet:rejected back to its option's
  // chip without scanning the bet store.
  const trackedBetsRef = useRef<Record<string, string>>({})

  // Tracks options whose chip was just placed — drives the entry
  // animation via the `--entering` modifier. The base chip has no
  // baseline `animation` (intentional), so toggling other modifier
  // classes (`--doubling`, `--confirmed`, `--failed`) doesn't restart
  // the entry keyframe when those modifiers come off. Each option's
  // entry is cleared after the animation duration so subsequent
  // re-renders can re-enter cleanly if the chip is re-placed.
  const [freshChips, setFreshChips] = useState<Record<string, true>>({})
  const previousPlacedRef = useRef<Record<string, PlacedChip>>({})
  useEffect(() => {
    const prev = previousPlacedRef.current
    const newlyPlaced: string[] = []
    for (const id of Object.keys(placedChips)) {
      if (!(id in prev)) newlyPlaced.push(id)
    }
    previousPlacedRef.current = placedChips
    if (newlyPlaced.length === 0) return
    setFreshChips((curr) => {
      const next = { ...curr }
      for (const id of newlyPlaced) next[id] = true
      return next
    })
    const timer = window.setTimeout(() => {
      setFreshChips((curr) => {
        let changed = false
        const next = { ...curr }
        for (const id of newlyPlaced) {
          if (id in next) {
            delete next[id]
            changed = true
          }
        }
        return changed ? next : curr
      })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [placedChips])

  const triggerSubmission = useCallback(
    async (opts?: { timerArmedAt?: number; hardLock?: BetLockSent }) => {
      // Atomic single-shot guard. Refs update synchronously so a second
      // caller (timer + race_started fallback firing on the same tick)
      // sees `true` and bails before any further work. Reset on
      // race_ready so the next round can submit.
      if (submissionStartedRef.current) return
      const chips = placedChipsRef.current
      if (Object.keys(chips).length === 0) return

      // B1 — frozen-tab hold. A backgrounded/frozen mobile tab pauses
      // setTimeout, so the armed 1.5s submit timer fires many seconds late —
      // by then the round it was queued for has closed, and submitting just
      // earns a guaranteed RoundClosed reject. Detect this by the timer's
      // WALL-CLOCK overrun (now - timerArmedAt): a freeze balloons it to
      // seconds, while a merely SLOW NETWORK does not (the timer runs on the
      // local clock, unaffected by request latency). Holding leaves
      // submissionStarted false so the chips carry forward and submit into the
      // next fresh round. Fails open when there's no armed timer to measure
      // (the race_started fallback) — never blocks a live bet on a slow link.
      if (isFrozenSubmission({ timerArmedAt: opts?.timerArmedAt, now: Date.now() })) {
        datadogLogs.logger.warn('chip_placement_submit_frozen_tab_held', {
          user_event: {
            timerArmedAt: opts?.timerArmedAt,
            elapsedMs: opts?.timerArmedAt ? Date.now() - opts.timerArmedAt : undefined
          }
        })
        // Latch the held round so the race_started fallback doesn't then submit
        // this same (now-closed) round.
        staleHeldRoundIdRef.current = currentRoundIdRef.current
        showNotice('Bets held for the next round')
        return
      }

      // Defence-in-depth balance check. The race_ready effect already
      // gates auto-bet against balance, and `handleOptionClick` blocks
      // over-balance manual placements — but the player's balance can
      // shift between race_ready and race_starting (e.g. a stake debit
      // landing late, an inventory rollback, or a settlement on a
      // parallel game). Re-check at submit time so we never push a
      // chip stack the player can't afford. If it fails here we stop
      // auto-bet, clear the chips, and surface the same notice as the
      // race_ready guard so the experience is consistent.
      // Safety: never submit before the real balance has loaded (defense-in-depth;
      // the per-click guard already blocks staking while loading).
      if (!balanceLoadedRef.current) {
        setAutoBet(false)
        setPlacedChips({})
        setAppliedBoosters({})
        return
      }
      // Only the cash-funded portion is checked against the wallet. Free-bet-covered
      // chips are paid by the reward (they hit the wire with amount 0, see
      // useChipPlacementBetSubmit) — counting them here wrongly aborted a free bet at
      // low/zero balance, clearing the chip and skipping the /bet. Mirrors the
      // free-bet-excluding rebet gate in the race_ready effect and the click-time guard.
      const chargeable = chargeableStake(chips, appliedFreeBetsRef.current)
      if (chargeable > balanceRef.current) {
        setAutoBet(false)
        setPlacedChips({})
        setAppliedBoosters({})
        showNotice('Insufficient balance — auto-bet stopped')
        emitBalanceInsufficient({
          requiredAmount: chargeable,
          currentBalance: balanceRef.current,
          currency
        })
        return
      }
      submissionStartedRef.current = true

      // Capture the round this submission belongs to. Used by the
      // post-submit cleanup paths (silent-failure strip + amount
      // reconciliation) to skip work if the round has changed during
      // the await — without this guard, chips placed on the new round
      // can be wrongly stripped.
      submittedRoundIdRef.current = currentRoundIdRef.current

      setBetsSubmitted(true)
      setLastBets(chips)
      setLastAppliedBoosters(appliedBoostersRef.current)

      // Mark every option about to be submitted as 'placing' BEFORE
      // the wire push, so the bet-store subscription watcher always
      // sees the option in tracking from the first bet:accepted onward.
      // Internal-only state (no visual) — drives the confirm/fail
      // resolution path below.
      const optionsBeingPlaced = Object.keys(chips)
      setSubmissionStates((prev) => {
        const next = { ...prev }
        for (const optionId of optionsBeingPlaced) {
          next[optionId] = 'placing'
        }
        return next
      })

      // A hard round's send waits for its channel's join until K − 0.5 s, no longer.
      const hardLock = opts?.hardLock
      const result = hardLock
        ? await submitAllBets(chips, appliedBoostersRef.current, appliedFreeBetsRef.current, {
            joinDeadline: stageTwoAt(hardLock.at, hardLock.lockLeadS).joinDeadline
          })
        : await submitAllBets(chips, appliedBoostersRef.current, appliedFreeBetsRef.current)
      const submittedIds = result.sent
      if (hardLock) setHardSentBetIds(submittedIds)
      // B2 — only mark the round as actually bet when something hit the wire.
      // The old code set betsActuallySent=true unconditionally, so a submit
      // that ABORTED (e.g. the player channel closed over on reconnect →
      // nothing sent) still made the chips look live all race and then vanish
      // at round end via the "clearAll" transition, though no bet was placed.
      const outcome = classifySubmitOutcome({
        sent: submittedIds.length,
        aborted: result.aborted,
        attemptedChips: Object.keys(chips).length
      })
      setBetsActuallySent(outcome.betsActuallySent)

      if (outcome.unlockForRetry) {
        // Transient submit failure put NOTHING on the wire. Unlock the
        // submission flags so the chips are NOT treated as a completed bet:
        // they stay on the board, carry forward, and re-submit next round
        // instead of disappearing. submissionStartedRef=false also lets the
        // race_started fallback retry within this same closing phase.
        setBetsSubmitted(false)
        submissionStartedRef.current = false
        submittedRoundIdRef.current = undefined
        showNotice("Couldn't place — holding for next round")
        datadogLogs.logger.warn('chip_placement_submit_aborted_retry', {
          user_event: { attemptedChips: Object.keys(chips).length }
        })
        return
      }

      // Build the betId → optionId map from the just-submitted bets so
      // the bet-store subscription effect can resolve incoming
      // bet:accepted / bet:rejected events back to their option's chip
      // without scanning the entire store on every change.
      const betStoreSnapshot = useBetStore.getState()
      const newTracked: Record<string, string> = { ...trackedBetsRef.current }
      for (const id of submittedIds) {
        const bet = betStoreSnapshot.bets.byId[id] ?? betStoreSnapshot.unacceptedBets[id]
        const optionId = bet?.betDetails?.selectedItems?.[0]
        if (optionId) newTracked[id] = optionId
      }
      trackedBetsRef.current = newTracked
      submittedBetIdsRef.current = submittedIds

      // Resolution happens via three independent paths, in priority order:
      //   1. The bet-store subscription watcher (defined below) — picks up
      //      `bet:accepted` / `bet:rejected` events as they arrive and
      //      updates per-option submission state immediately.
      //   2. The `race_started` reconciliation effect — runs once
      //      `race_started + RACE_STARTED_RECONCILE_DELAY_MS` has elapsed
      //      to catch bets whose `bet:accepted` event was lost in transit
      //      but ARE present in the authoritative `currentBets` snapshot.
      //   3. The `race_settled` reconciliation effect — final safety net
      //      that resolves any still-pending bets one way or the other
      //      before round teardown.
      // The previous 2.5s wall-clock timer was the root of the false-
      // rejection bug: any bet whose end-to-end latency exceeded 2.5s
      // was declared silent-failed even though the server had accepted
      // it (5s grace in BettingService.CanPlaceBet). The new design
      // reconciles against server truth instead of guessing on a timer.
    },
    [submitAllBets, showNotice, currency]
  )

  // B3 — latest-ref for the submission trigger. `triggerSubmission`'s
  // identity churns whenever `submitAllBets` does (it closes over `game` /
  // `player`, whose references change on unrelated store updates). If the
  // trigger effect below depended on the callback, that churn would re-run
  // the effect during `race_starting`, clear the armed 1.5s timer and
  // re-arm a fresh one — repeatedly, so it never fires and submission drifts
  // to the race_started fallback that lands AT close → reject. Reading the
  // callback through a ref keeps the effect keyed on the PHASE only.
  const triggerSubmissionRef = useRef(triggerSubmission)
  triggerSubmissionRef.current = triggerSubmission

  // Subscribe to the bet store so we can resolve each tracked bet
  // the moment its outcome lands:
  //   - bet:accepted   → bet moves into `bets.byId`, leaves
  //                      `unacceptedBets` → mark option `confirmed`
  //                      (~400ms pulse, then back to idle).
  //   - bet:rejected   → bet leaves `unacceptedBets` and is NOT in
  //                      `bets.byId` → mark option `failed` (~400ms
  //                      shake, then chip clears).
  //   - silent failure → bet sits in `unacceptedBets` past the
  //                      reconciliation effects below; those reconcile
  //                      against `currentBets` and either pull the bet
  //                      back into `bets.byId` (lost-event recovery) or
  //                      declare a final silent-failure cleanup.
  // Mounted once at panel mount — refs (`trackedBetsRef`) keep it in
  // sync with the latest submission without re-subscribing.
  useEffect(() => {
    return useBetStore.subscribe((state) => {
      const tracked = trackedBetsRef.current
      const trackedIds = Object.keys(tracked)
      if (trackedIds.length === 0) return

      for (const id of trackedIds) {
        const stillUnaccepted = state.unacceptedBets[id] !== undefined
        if (stillUnaccepted) continue
        const optionId = tracked[id]
        if (!optionId) {
          delete tracked[id]
          continue
        }
        const accepted = state.bets.byId[id] !== undefined
        delete tracked[id]
        if (accepted) {
          // Confirmed — short pulse on the chip then back to idle.
          setSubmissionStates((prev) => {
            // Don't downgrade an already-failed option (e.g. some
            // marginal race where reject + accept both fire).
            if (prev[optionId] === 'failed') return prev
            return { ...prev, [optionId]: 'confirmed' }
          })
          window.setTimeout(() => {
            setSubmissionStates((prev) => {
              if (prev[optionId] !== 'confirmed') return prev
              const next = { ...prev }
              delete next[optionId]
              return next
            })
          }, 450)
        } else {
          // Rejected by the server — chip clears after a brief
          // shake. Same cleanup shape as the silent-failure path
          // in triggerSubmission so all "failed" outcomes look the
          // same to the player.
          setSubmissionStates((prev) => ({ ...prev, [optionId]: 'failed' }))
          // A hard round's late answer (`NotInTime`): no bet, and its stake
          // is being rolled back. Say so: this board shows no reason toast.
          const late = state.lastRejection
          if (late?.betId === id && late.detail === RejectionDetails.NotInTime) {
            showNotice(notInTimeTextRef.current, 'warning', 4000)
          }
          window.setTimeout(() => {
            setPlacedChips((prev) => {
              if (!(optionId in prev)) return prev
              const next = { ...prev }
              delete next[optionId]
              return next
            })
            setAppliedBoosters((prev) => {
              if (!(optionId in prev)) return prev
              const next = { ...prev }
              delete next[optionId]
              return next
            })
            setSubmissionStates((prev) => {
              if (prev[optionId] !== 'failed') return prev
              const next = { ...prev }
              delete next[optionId]
              return next
            })
          }, 500)
        }
      }
    })
  }, [])

  // Reconcile any tracked bets that are still in `unacceptedBets`
  // against the authoritative server-broadcast `currentBets` list.
  // Two cases:
  //   - Bet IS in `currentBets`  → server accepted it but the
  //     `bet:accepted` WS event was lost or arrived too late. Sync
  //     local state via `actions.bet.betAccepted`; the subscription
  //     watcher above will fire on the resulting state change and
  //     mark the option `confirmed` for the player.
  //   - Bet is NOT in `currentBets` → server either rejected it
  //     (no `bet:rejected` arrived either) or genuinely silent-
  //     failed. Refund the optimistic balance debit, shake + clear
  //     the chip, and log to Datadog.
  // `reason` is forwarded into the silent-failure log so we can
  // separate the race_started reconciliation pass (~5s post-close,
  // covers most of the latency tail) from the race_settled safety
  // net (catches anything that slipped through).
  const reconcileUnacceptedBets = useCallback(
    (reason: 'race_started' | 'race_settled') => {
      // Don't reconcile if the round we submitted for is no longer
      // current — by then the player has moved on, and any chips
      // they have placed on the new round are unrelated to this
      // submission's pending bets.
      if (submittedRoundIdRef.current !== currentRoundIdRef.current) return

      const tracked = trackedBetsRef.current
      const trackedIds = Object.keys(tracked)
      if (trackedIds.length === 0) return

      const state = useBetStore.getState()
      const stuckIds: string[] = []
      const stuckOptionIds = new Set<string>()

      // Only judge at settlement. A real bet — win or lose — leaves
      // `unacceptedBets` at PLACEMENT (its `bet:accepted` arrives there), so by
      // race_settled anything still pending was never registered server-side.
      // Judging earlier risked clearing a real bet whose ack was merely slow
      // (the old 2.5s-timer bug); the previous `currentBets` recovery check was
      // also circular (the same local store), so it falsely "confirmed" bets the
      // server never registered. The authoritative purge + refund now lives in
      // the global race_settled handler (isUnregisteredAtSettlement); here we
      // only surface the 'failed' chip so the player sees it didn't go through.
      if (reason !== 'race_settled') return

      for (const betId of trackedIds) {
        // Resolved in real time by the subscription watcher (a real
        // bet:accepted / bet:rejected arrived) — nothing to surface.
        if (state.unacceptedBets[betId] === undefined) continue

        // Still pending at settlement ⇒ never registered.
        stuckIds.push(betId)
        const bet = state.unacceptedBets[betId]
        const optionId = bet?.betDetails?.selectedItems?.[0]
        if (optionId) stuckOptionIds.add(optionId)
        datadogLogs.logger.warn('chip_placement_bet_silent_failure', {
          user_event: {
            betId,
            optionId: bet?.betDetails?.selectedItems?.[0],
            amount: bet?.betAmount,
            reason
          }
        })
        // Drop tracking so this bet isn't reprocessed by a later pass.
        delete tracked[betId]
      }

      if (stuckIds.length === 0) return

      // Same shake-then-clear sequence as the bet:rejected path in
      // the subscription watcher, so server-rejected and
      // silent-failure outcomes look identical to the player.
      setSubmissionStates((prev) => {
        const next = { ...prev }
        for (const optionId of stuckOptionIds) {
          next[optionId] = 'failed'
        }
        return next
      })

      window.setTimeout(() => {
        if (submittedRoundIdRef.current !== currentRoundIdRef.current) return
        setPlacedChips((prev) => {
          let changed = false
          const next: Record<string, PlacedChip> = {}
          for (const [optionId, chip] of Object.entries(prev)) {
            if (stuckOptionIds.has(optionId)) {
              changed = true
              continue
            }
            next[optionId] = chip
          }
          return changed ? next : prev
        })
        setAppliedBoosters((prev) => {
          let changed = false
          const next: Record<string, { boosterId: string; percent: number }> = {}
          for (const [optionId, b] of Object.entries(prev)) {
            if (stuckOptionIds.has(optionId)) {
              changed = true
              continue
            }
            next[optionId] = b
          }
          return changed ? next : prev
        })
        setSubmissionStates((prev) => {
          const next = { ...prev }
          for (const optionId of stuckOptionIds) {
            if (next[optionId] === 'failed') delete next[optionId]
          }
          return next
        })
      }, 500)

      // If every submitted bet for this round silent-failed, unlock
      // the UI so the player can place fresh chips during the race.
      // Mirrors the all-fail branch of the old 2.5s-timer path so the
      // round-transition handler downstream still does the right
      // thing (keeps chips, doesn't auto-rebet a failed submission).
      const submittedCount = submittedBetIdsRef.current.length
      if (submittedCount > 0 && stuckIds.length === submittedCount) {
        showNotice("Couldn't place — please try again next round")
        setBetsSubmitted(false)
        setBetsActuallySent(false)
        submissionStartedRef.current = false
      } else if (stuckIds.length > 0) {
        showNotice(`${stuckIds.length} of ${submittedCount} didn't go through`)
      }
    },
    [playerActions, showNotice]
  )

  // B3 — latest-ref for reconciliation, same rationale as the trigger:
  // keep the phase-driven reconcile effects below from re-running (and
  // resetting their timers) on unrelated callback-identity churn.
  const reconcileRef = useRef(reconcileUnacceptedBets)
  reconcileRef.current = reconcileUnacceptedBets

  // Phase-aware reconciliation #1 — race_started + grace.
  //
  // Why race_started: it's the authoritative "betting is now closed"
  // signal from the server. Any bet that the server was going to
  // accept has been accepted by `race_started + 5s` at the latest
  // (matches the wall-clock guard in BettingService.CanPlaceBet,
  // betting.go:45-49). Reconciling at this point catches the bulk
  // of the latency tail with a single deterministic event instead
  // of a wall-clock timer running from submit-time.
  //
  // Grace delay: the 5s constant tracks the backend grace. If that
  // backend constant is ever tightened, lower this in lockstep.
  useEffect(() => {
    if (latestEvent?.eventType !== 'race_started') return
    if (submittedRoundIdRef.current !== currentRoundIdRef.current) return
    reconcileTimerRef.current = setTimeout(() => {
      reconcileRef.current('race_started')
    }, RACE_STARTED_RECONCILE_DELAY_MS)
    return () => {
      if (reconcileTimerRef.current) {
        clearTimeout(reconcileTimerRef.current)
        reconcileTimerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestEvent?.eventType])

  // Phase-aware reconciliation #2 — race_settled safety net.
  //
  // By the time race_settled fires the race outcome is known and
  // no further bet:accepted events for this round will ever arrive.
  // Anything still in unacceptedBets is definitively a silent
  // failure. This catches edge cases the race_started pass missed
  // (player disconnected during the race, currentBets snapshot was
  // stale at race_started, server-side late accept that beat the
  // backend grace, etc.).
  useEffect(() => {
    if (latestEvent?.eventType !== 'race_settled') return
    if (submittedRoundIdRef.current !== currentRoundIdRef.current) return
    reconcileRef.current('race_settled')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestEvent?.eventType])

  // Tracks whether we have actually observed `race_ready` for the
  // current round in this panel's lifetime. Without this guard, a
  // player who refreshes / joins during `race_started` (or later)
  // would have their freshly-placed "queued for next round" chips
  // auto-submitted against the ALREADY-CLOSED current round —
  // bet-registrator rejects them with `RoundClosed`. The ref flips
  // to true whenever we see `race_ready`, and back to false on
  // round transition (new race_ready fires for the new round =
  // ref stays true). Restored bets from `currentBets` already
  // skip auto-submit via the restoration effect setting
  // `betsSubmitted=true` + `submissionStartedRef.current` is
  // unaffected; this guard is purely for the "joined cold,
  // placed a chip" path.
  const observedBettingOpenRef = useRef(false)

  // B4 — recover from a dropped `race_ready`. Its per-round reset
  // (below) never ran, so last round's completed-submission flags
  // survive and the single-shot guard would silently block THIS round
  // from ever submitting. The submittedRoundId != currentRoundId guard
  // inside the helper is essential — resetting mid-round would disarm
  // the settle reconcile. Run at race_starting, and at a hard round's lock
  // before it sends (a hard round that missed race_ready locks on
  // race_starting's arrival). Reads only refs and stable setters, so the
  // ref below always holds a correct copy. True when it cleared last round's
  // completed board: the state update is not drawn yet, so a send in the same
  // tick must not read `placedChipsRef` (`hardLockBoard`).
  const recoverMissedReadyReset = (): boolean => {
    if (
      shouldRecoverMissedRoundReset({
        submissionStarted: submissionStartedRef.current,
        submittedRoundId: submittedRoundIdRef.current,
        currentRoundId: currentRoundIdRef.current
      })
    ) {
      const hadSuccessfulSubmission = betsActuallySentRef.current
      datadogLogs.logger.warn('chip_placement_missed_race_ready_recovered', {
        user_event: {
          submittedRoundId: submittedRoundIdRef.current,
          currentRoundId: currentRoundIdRef.current,
          hadSuccessfulSubmission
        }
      })
      setBetsSubmitted(false)
      setBetsActuallySent(false)
      submissionStartedRef.current = false
      submittedRoundIdRef.current = undefined
      submittedBetIdsRef.current = []
      setSubmissionStates((prev) => (Object.keys(prev).length === 0 ? prev : {}))
      trackedBetsRef.current = {}
      // Mirror the race_ready board decision so recovery never silently
      // RE-BETS chips that were already placed. A completed manual bet
      // last round means those chips are done → clear them (clearAll), so
      // the timer below finds an empty board and submits nothing. If
      // nothing was submitted (queued placements / all-failed), keep them
      // — they're this round's intent and the timer re-submits them. Auto-
      // bet keeps its board (rebet) regardless.
      if (hadSuccessfulSubmission && !autoBetRef.current) {
        setPlacedChips((prev) => (Object.keys(prev).length === 0 ? prev : {}))
        setAppliedBoosters((prev) => (Object.keys(prev).length === 0 ? prev : {}))
        setAppliedFreeBets((prev) => (Object.keys(prev).length === 0 ? prev : {}))
        return true
      }
    }
    return false
  }
  const recoverMissedReadyResetRef = useRef(recoverMissedReadyReset)
  recoverMissedReadyResetRef.current = recoverMissedReadyReset

  // The hard lock (see `useBetLock` above): every option goes out now, once.
  // A board that never saw this round open sends nothing (as at race_starting
  // below), and a countdown held back by a frozen tab holds the chips for the
  // next round (today's frozen-tab check, on the countdown timer).
  onHardLockRef.current = (sent: BetLockSent) => {
    if (!observedBettingOpenRef.current) return
    const recoveryClearedBoard = recoverMissedReadyResetRef.current()
    if (sent.frozen) {
      staleHeldRoundIdRef.current = currentRoundIdRef.current
      datadogLogs.logger.warn('chip_placement_submit_frozen_tab_held', {
        user_event: { hardClose: true }
      })
      showNotice('Bets held for the next round')
      return
    }
    // The send reads `placedChipsRef` in this very tick: not a board the
    // recovery just cleared, nor one rebuilt from this round's own bets.
    const board = hardLockBoard({
      chips: placedChipsRef.current,
      currentRoundId: currentRoundIdRef.current,
      restoredRoundId: restoredRoundIdRef.current,
      recoveryClearedBoard
    })
    if (Object.keys(board).length === 0) return
    void triggerSubmissionRef.current({ hardLock: sent })
  }

  // Single submission trigger — schedule a timer at race_starting,
  // fall back to immediate submit at race_started if the timer hasn't
  // fired yet. The ref-gate inside triggerSubmission ensures we only
  // ever submit once per round.
  useEffect(() => {
    const phase = latestEvent?.eventType

    if (phase === 'race_ready') {
      observedBettingOpenRef.current = true
      // New round — clear any freshness-hold latch from the previous round.
      staleHeldRoundIdRef.current = undefined
    }
    if (phase === 'race_starting') {
      if (!observedBettingOpenRef.current) {
        // Mounted into the closing phase without ever seeing
        // race_ready for this round — chips on the board are the
        // player's intent for the NEXT round, not this one. Skip
        // the auto-submit; race_ready for the next round will
        // arm the timer normally.
        return
      }
      // B4 — recover from a dropped `race_ready` (see `recoverMissedReadyReset`).
      recoverMissedReadyResetRef.current()
      // A hard round has no 1.5 s timer: it sends at its lock (`onHardLock`).
      if (betLock.hard) return
      // Submit ~1.5s into the closing phase. Phase typically lasts
      // 3-5s, so this leaves ~1.5-3.5s buffer before the round
      // actually starts (and closes server-side). The earlier
      // submission gives the backend more headroom to register +
      // broadcast bet:accepted before close, so the silent-failure
      // path triggers far less often. Late chip placements during
      // the closing phase are rare in practice (the countdown
      // reads "betting closing" to the player).
      // Capture the wall-clock arm time so the timer callback can detect a
      // freeze by its own overrun (see isFrozenSubmission). A slow network
      // does not inflate this; a paused/frozen tab does.
      const armedAt = Date.now()
      submitTimerRef.current = setTimeout(() => {
        void triggerSubmissionRef.current({ timerArmedAt: armedAt })
      }, SUBMIT_TIMER_MS)
      return () => {
        if (submitTimerRef.current) {
          clearTimeout(submitTimerRef.current)
          submitTimerRef.current = null
        }
      }
    }
    if (phase === 'race_started') {
      if (!observedBettingOpenRef.current) {
        // Same reasoning — joined past the betting window. Don't
        // submit; chips queue for the next round.
        return
      }
      // A hard round has no race_started fallback: a board that did not lock
      // sends nothing, and its chips carry to the next round.
      if (betLock.hard) return
      // The timer path already held this round as frozen (B1). Don't let the
      // fallback then submit this same (now-closed) round — the chips carry
      // forward to the next round instead.
      if (staleHeldRoundIdRef.current === currentRoundIdRef.current) return
      // No armed timer here → no freeze measurement → submits (fail open). A
      // rare freeze-into-race_started just yields the pre-B1 reject+refund,
      // never a wrongly-held live bet.
      void triggerSubmissionRef.current()
    }
    // Keyed on the PHASE only (see B3): the callback is read through a ref so
    // callback-identity churn can't re-run this effect and reset the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestEvent?.eventType])

  // Reconciliation. After submission, sync the chip amounts to
  // whatever the bet store reports for any chip that's still on the
  // board. We DO NOT strip chips here — round transitions briefly
  // present an empty `currentBets` (new race, no bets yet), and
  // stripping in that window clobbers chips that should persist
  // into the new round. The silent-failure check in
  // `triggerSubmission` handles "push didn't go through" cases;
  // server rejections (`bet:rejected` event) trigger their own
  // notification flow via `handleBetRejected` and update the bet
  // store.
  useEffect(() => {
    if (!betsActuallySent) return
    if (submittedRoundIdRef.current !== currentRoundIdRef.current) return
    setPlacedChips((prev) => {
      let changed = false
      const next: Record<string, PlacedChip> = {}
      for (const [optionId, chip] of Object.entries(prev)) {
        const bet = currentBets.find((b) => b.betDetails?.selectedItems?.[0] === optionId)
        if (bet && bet.betAmount !== chip.amount) {
          next[optionId] = { ...chip, amount: bet.betAmount }
          changed = true
        } else {
          next[optionId] = chip
        }
      }
      return changed ? next : prev
    })
  }, [betsActuallySent, currentBets])

  // Round transition: when betting opens for the new round, reset
  // submission flags. Chip-clearing behaviour:
  //
  //   autoBet ON: chips persist (auto-rebet semantics).
  //   betsActuallySent=true (last round had a successful submission
  //     cycle — at least one bet went through): clear chips + boosters.
  //     Fresh board, player picks new options.
  //   betsActuallySent=false (no submission happened, or all bets
  //     failed in the silent-failure handler): keep chips. Either
  //     they were never bets to begin with, or the player placed
  //     them after the all-fail unlock and they're queued for the
  //     new round.
  //
  // The silent-failure check inside `triggerSubmission` (~2.5s after
  // submission) has already stripped chips whose bets didn't make
  // it to the server, so when we DO clear, only the "real" chips
  // are being cleared.
  //
  // On a hard round it is keyed on the round id too: a page that drew nothing
  // of a hard round after its lock (refetched in one go) goes from that round's
  // `race_ready` straight to the next round's. The phase alone does not change
  // then, and the next round would open still locked on the last round's bet.
  // Undefined on a round that is not hard (`useBetLock`), so those rounds run
  // exactly as before.
  const hardRoundId = betLock.roundId
  useEffect(() => {
    if (latestEvent?.eventType !== 'race_ready') return
    // A hard round's own send (or its board rebuilt from its own bets) while
    // `race_ready` is still the latest row: not a new round. The board keeps
    // the round's bets, locked, until the next `race_ready`.
    if (
      isOwnRoundRerun({
        hard: betLockHardRef.current,
        currentRoundId: hardRoundId,
        submittedRoundId: submittedRoundIdRef.current,
        restoredRoundId: restoredRoundIdRef.current
      })
    ) {
      return
    }
    const hadSuccessfulSubmission = betsActuallySent

    setBetsSubmitted(false)
    setBetsActuallySent(false)
    submissionStartedRef.current = false
    submittedRoundIdRef.current = undefined
    submittedBetIdsRef.current = []
    // Cancel any pending reconciliation timer scheduled by the
    // previous round's race_started handler — a fresh race_ready
    // means we've already moved past the round those bets belonged
    // to, and the reconcileUnacceptedBets call would early-out on
    // the round-id guard anyway. Clearing here avoids a no-op
    // timer firing during the new round and keeps the timer ref
    // consistent with the lifecycle reset below.
    if (reconcileTimerRef.current) {
      clearTimeout(reconcileTimerRef.current)
      reconcileTimerRef.current = null
    }
    // Submission state map and the betId-tracking dictionary are
    // per-round — clear them here so the next round's submission
    // starts from a clean slate. Chips themselves are handled
    // below; we only touch the lifecycle flags here.
    // Use the prev-aware updater so an already-empty map stays the
    // same reference (no spurious re-render → no chance of looping
    // through this effect when an upstream dep is unstable).
    setSubmissionStates((prev) => (Object.keys(prev).length === 0 ? prev : {}))
    trackedBetsRef.current = {}
    setHardSentBetIds((prev) => (prev.length === 0 ? prev : []))

    // Auto-bet balance guard. Manual bet placement already checks
    // balance per-click (see `handleOptionClick`), but the auto-bet
    // rebet path skips that check entirely and just re-fires last
    // round's chips on every new round. If the player's balance has
    // dropped below what the persisted chip stack costs, we'd be
    // queueing bets the operator can only reject — and on
    // soft-balance currencies (XXX play-money) the rejection isn't
    // even guaranteed, so the displayed balance can drift negative.
    // Stop the loop here, clear the chips, and surface a notice so
    // the player understands why auto-bet flipped off.
    //
    // Note: free-bet stakes are excluded from the balance check below —
    // they didn't come out of the wallet last round and the strip
    // happening just below removes those chips from the rebet anyway.
    if (autoBet) {
      const persistedChips = placedChipsRef.current
      const persistedFreeBets = appliedFreeBetsRef.current
      const stakedTotal = Object.values(persistedChips).reduce(
        (sum, c) => (persistedFreeBets[c.optionId] ? sum : sum + c.amount),
        0
      )
      if (stakedTotal > 0 && stakedTotal > balanceRef.current) {
        setAutoBet(false)
        setPlacedChips((prev) => (Object.keys(prev).length === 0 ? prev : {}))
        setAppliedBoosters((prev) => (Object.keys(prev).length === 0 ? prev : {}))
        setAppliedFreeBets((prev) => (Object.keys(prev).length === 0 ? prev : {}))
        showNotice('Insufficient balance — auto-bet stopped')
        emitBalanceInsufficient({
          requiredAmount: stakedTotal,
          currentBalance: balanceRef.current,
          currency
        })
        return
      }
    }

    const transition = roundTransitionAction({ autoBet, hadSuccessfulSubmission })

    if (transition === 'clearAll') {
      // Fresh round after a completed manual bet — clear chips + boosters + free bets.
      setPlacedChips((prev) => (Object.keys(prev).length === 0 ? prev : {}))
      setAppliedBoosters((prev) => (Object.keys(prev).length === 0 ? prev : {}))
      setAppliedFreeBets((prev) => (Object.keys(prev).length === 0 ? prev : {}))
      return
    }

    if (transition === 'carryForward') {
      // Nothing was submitted last round (auto-bet off) — the chips on the board
      // are unfinished placements QUEUED for the next round (placed while betting
      // was closed). Carry them forward untouched — chips, free bets AND boosters
      // — so they submit at the next round start exactly as placed, identical to
      // how queued cash chips already behave. Previously the free-bet strip below
      // also ran here and wrongly dropped a queued free-bet chip, so a free bet
      // placed during an in-progress round silently vanished with no /bet sent.
      return
    }

    // transition === 'rebet' (auto-bet on). Strip any chip whose option had a
    // free bet attached — that free bet was consumed by last round's RegisterBet
    // and the inventory item is gone. Per-spec: those options just go
    // empty for the next round (the player can re-place if they have
    // another free bet, otherwise it's a normal stake decision). Normal-
    // stake chips persist unchanged.
    setPlacedChips((prev) => {
      const freeBets = appliedFreeBetsRef.current
      const stripped: Record<string, PlacedChip> = {}
      let changed = false
      for (const [key, chip] of Object.entries(prev)) {
        if (freeBets[key]) {
          changed = true
          continue
        }
        stripped[key] = chip
      }
      return changed ? stripped : prev
    })
    // Free bets are always consumed across a round transition — clear
    // the map so the next round starts clean. Any newly-arrived free
    // bets in inventory will auto-claim on the next chip placement.
    setAppliedFreeBets((prev) => (Object.keys(prev).length === 0 ? prev : {}))

    // Re-bind boosters from the current available pool — last round's
    // boosters may have been consumed by the bets that did go through, so
    // chips that survived get freshly assigned in option-order, mirroring
    // `handleOptionClick`'s auto-claim.
    setAppliedBoosters((prev) => {
      const chips = placedChipsRef.current
      const optionIds = Object.keys(chips)
      if (optionIds.length === 0) {
        // Already empty? Keep prev to avoid spurious re-renders on
        // every effect-firing.
        return Object.keys(prev).length === 0 ? prev : {}
      }
      const boosters = availableBoostersRef.current
      const next: Record<string, { boosterId: string; percent: number }> = {}
      for (let i = 0; i < optionIds.length && i < boosters.length; i++) {
        const optionId = optionIds[i]!
        const booster = boosters[i]!
        next[optionId] = { boosterId: booster.id, percent: booster.boost.value }
      }
      return next
    })
  }, [latestEvent?.eventType, hardRoundId, autoBet, betsActuallySent, showNotice, currency])

  // Save bets when cleared (simulates round end saving last bets)
  const handleClearAllWithSave = useCallback(() => {
    if (Object.keys(placedChips).length > 0) {
      disableAutoBetOnEdit()
      setLastBets(placedChips)
      setLastAppliedBoosters(appliedBoosters)
    }
    setPlacedChips({})
    setAppliedBoosters({})
    setAppliedFreeBets({})
  }, [placedChips, appliedBoosters, disableAutoBetOnEdit])

  const chipCount = Object.keys(placedChips).length

  const isPreBetting = !isBettingOpen && !betsSubmitted && chipCount >= 0

  // Stable callback props for `<SideBetRows>` so its internal
  // computations (and any future `React.memo`) aren't busted by
  // fresh closures on every panel render.
  const sideBetGetChipStyle = useCallback(
    (amt: number) => getChipStyleForAmount(amt, chips),
    [chips]
  )
  const sideBetFormatAmount = useCallback((amt: number) => formatNumberWithSuffix(amt, t), [t])
  const sideBetOnPlace = useCallback(
    (p: {
      marketId: string
      optionId: string
      betType: BetType
      selectedItems: string[]
      multiplier: number
      label: string
    }) =>
      handleOptionClick(p.marketId, p.optionId, {
        betType: p.betType,
        selectedItems: p.selectedItems,
        multiplier: p.multiplier,
        label: p.label
      }),
    [handleOptionClick]
  )
  const sideBetWinPillBaseDelayMs = useMemo(
    () => Math.max(0, totalWinDelayMs - PILL_COUNTUP_TAIL_MS) + 120,
    [totalWinDelayMs]
  )

  return (
    <div
      className={`chip-panel ${betsSubmitted ? 'chip-panel--submitted' : ''} ${collapseToPlacedBets ? 'chip-panel--collapsed' : ''} ${isCCTVGameType(game.track.gameType) ? 'chip-panel--cctv' : ''}`}
    >
      {/* Persistent CCTV disclaimer — sits above the transient
          notice slot so both can render simultaneously. When the
          transient slot is empty, this is the only pill in the
          slot stack. When a warning fires (e.g. "Max bet reached"),
          it lands below this disclaimer rather than replacing it. */}
      {showCctvDisclaimer && cctvDisclaimerCopy ? (
        <div
          key="cctv-disclaimer"
          className="chip-panel__notice chip-panel__notice--success chip-panel__notice--persistent"
          role="status"
          aria-live="polite"
        >
          {/* Inline info glyph — the chip-panel__notice base styles
              hide the ::before pseudo-element on the persistent
              variant so this SVG sits as the only icon. */}
          <InfoCircleIcon className="chip-panel__notice-icon" />
          {cctvDisclaimerCopy}
        </div>
      ) : null}
      {/* Stage 2 of a hard round (DESIGN §6): from the lock, while a bet it
          sent is unanswered, "Locked · Placing bet..."; from the board's
          estimate of K, "Confirming with your casino". Each option's ✓ or
          refusal stays on its chip. */}
      {stageTwo ? (
        <div
          key={`stage-two:${stageTwo}`}
          className="chip-panel__notice chip-panel__notice--info chip-panel__notice--persistent"
          role="status"
          aria-live="polite"
        >
          {stageTwo === 'placing'
            ? `${t('Locked')} · ${t('Placing bet...')}`
            : t('Confirming with your casino')}
        </div>
      ) : null}
      {/* Transient notice slot. Replaces the "Placing for next round"
          label while it's visible so we don't stack two strips of
          small text on top of each other. The notice uses a `key`
          tied to its own state so React remounts the node when a
          new message arrives — that retriggers the entrance
          animation even on rapid duplicates. */}
      {notice ? (
        <div
          key={`${notice.tone}:${notice.message}`}
          className={`chip-panel__notice chip-panel__notice--${notice.tone}`}
          role="status"
          aria-live="polite"
        >
          {notice.message}
        </div>
      ) : isSettled && totalWinAmount > 0 ? (
        // Capstone "TOTAL WIN" banner shown when the player won at
        // least one bet in the settled round. Fills the same vertical
        // slot the notice/next-round-label uses so it never pushes
        // the markets/chip-bar around. Delayed entry so it lands
        // after the per-option pills have finished counting up.
        <TotalWinBanner
          amount={totalWinAmount}
          currencySymbol={currencySymbol}
          delayMs={totalWinDelayMs}
        />
      ) : (
        isPreBetting &&
        chipCount > 0 && (
          <div className="chip-panel__next-round-label">{t('Placing for next round')}</div>
        )
      )}
      {/* ── Markets ── */}
      {/* `--selected-chip-color/-bg` are propagated to the option
          buttons so the hover-preview ring (CSS `::after`) can paint
          itself in the colour of whichever chip the player has
          currently selected on the chip strip. Lets the player see
          "tap and that chip lands here" before they commit. */}
      <div
        className={`chip-panel__board ${markets.filter((m) => m.type === 'main').length > 1 ? 'chip-panel__board--multi' : ''}`}
        style={
          {
            '--selected-chip-color': selectedChip.border,
            '--selected-chip-bg': selectedChip.bg
          } as React.CSSProperties
        }
      >
        {markets
          .filter((m) => m.type === 'main')
          .map((market, idx) => (
            <React.Fragment key={market.id}>
              {idx === 1 && <div className="chip-panel__divider" />}
              <div className="chip-panel__market">
                <div
                  className="chip-panel__market-options"
                  data-option-count={market.options.filter((o) => o.bettable).length}
                >
                  {market.options
                    .filter((o) => o.bettable)
                    .map((option) => {
                      const placed = placedChips[option.id]
                      // Boost visuals + multiplier display gated on
                      // `lightningBoostApplies` so chips queued for the next
                      // round don't pick up the current round's lightning
                      // state. See `lightningBoostApplies` declaration for
                      // the full rationale.
                      const isBoosted =
                        lightningBoostApplies && lightningBoostedItems.has(option.id)
                      // Settlement-time decoration. `isWinningOption` lights
                      // up the option (gold ring + tag) regardless of whether
                      // the player bet on it, so the round outcome is always
                      // visible. `myBet` only fires the "you won/lost" chip
                      // morph when the player actually has skin in the game.
                      const isWinningOption = isSettled && winningOptionIds.has(option.id)
                      const myBet = myBetByOptionId.get(option.id)
                      const myBetWon = myBet?.status === 'won'
                      const myBetLost = myBet?.status === 'lost'
                      return (
                        <button
                          key={option.id}
                          ref={(el) => {
                            optionRefs.current[option.id] = el
                          }}
                          className={[
                            'chip-panel__opt',
                            placed ? 'chip-panel__opt--placed' : '',
                            betsSubmitted ? 'chip-panel__opt--locked' : '',
                            isBoosted ? 'chip-panel__opt--lightning' : '',
                            isBoosted && isLightningRevealing
                              ? 'chip-panel__opt--lightning-revealing'
                              : '',
                            isWinningOption ? 'chip-panel__opt--winner' : '',
                            receivingOption === option.id ? 'chip-panel__opt--receiving' : '',
                            isRaceLive && placed ? 'chip-panel__opt--racing' : '',
                            collapseToPlacedBets && !placed ? 'chip-panel__opt--hidden' : '',
                            myBetWon ? 'chip-panel__opt--my-win' : '',
                            myBetLost ? 'chip-panel__opt--my-loss' : ''
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => !betsSubmitted && handleOptionClick(market.id, option.id)}
                          disabled={betsSubmitted}
                          style={
                            {
                              // Stagger the ambient sheen sweep across
                              // options so they don't fire in lockstep —
                              // gives the row a "live stage" feel rather
                              // than a marching-band ripple. Index-based
                              // (not random) so the offset is stable
                              // across re-renders.
                              '--shine-delay': `${optionIndexById.get(option.id) ?? 0}s`
                            } as React.CSSProperties
                          }
                        >
                          <span className="chip-panel__opt-shine" aria-hidden="true" />
                          {/* Winning-option pill — Evolution-style call-out
                            with two states sharing one chassis: "WINNER"
                            for any winning option (even when the player
                            didn't bet, so the round outcome stays visible
                            on the board), or the player's animated
                            payout when they backed the winner.
                            Stagger-cascade across the row via `delayMs`
                            so multi-option settles read as choreographed,
                            not a synchronised burst. */}
                          {isWinningOption && (
                            <WinPill
                              isPayout={myBetWon}
                              payoutAmount={myBet?.potentialPayout ?? 0}
                              currencySymbol={currencySymbol}
                              tier={myBet ? tierForBet(myBet) : 'win'}
                              delayMs={(optionIndexById.get(option.id) ?? 0) * STAGGER_MS}
                            />
                          )}
                          {/* 2nd/3rd place tag — minimal, restrained pill that
                            preserves podium context without claiming the
                            option won. Renders only on settled rounds for
                            options that finished 2nd or 3rd. The full
                            WinPill is reserved for position 1. */}
                          {isSettled &&
                            !isWinningOption &&
                            (() => {
                              const place = placementByOptionId.get(option.id)
                              if (place !== 2 && place !== 3) return null
                              return (
                                <span
                                  className={`chip-panel__opt-place-tag chip-panel__opt-place-tag--${place === 2 ? 'second' : 'third'}`}
                                  style={
                                    {
                                      '--win-pill-delay': `${(optionIndexById.get(option.id) ?? 0) * STAGGER_MS}ms`
                                    } as React.CSSProperties
                                  }
                                >
                                  <span className="chip-panel__opt-place-tag-text">
                                    {t(place === 2 ? '2nd' : '3rd').toUpperCase()}
                                  </span>
                                </span>
                              )
                            })()}
                          {/* Lightning-strike SVG overlay — fires the
                            moment the reveal lands. Two zigzag bolt
                            paths (one main, one short branch) draw in
                            via stroke-dashoffset, glow with a layered
                            white-core / gold-halo gradient, then fade.
                            Sequenced BEFORE the boost-pill / Pays
                            number reveal so the eye sees the strike
                            first, then the magnitude — the Lightning-
                            Dice-style "the lightning hit, here's what
                            it did" beat. Only renders during the
                            reveal animation window for boosted options. */}
                          {isBoosted && isLightningRevealing && (
                            <svg
                              className="chip-panel__opt-bolt"
                              viewBox="0 0 100 100"
                              preserveAspectRatio="xMidYMid meet"
                              aria-hidden="true"
                            >
                              <defs>
                                <linearGradient id="bolt-gradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#ffffff" />
                                  <stop offset="55%" stopColor="#fff8d4" />
                                  <stop offset="100%" stopColor="#fde047" />
                                </linearGradient>
                              </defs>
                              {/* Soft outer glow halo (drawn first, behind the core). */}
                              <path
                                className="chip-panel__opt-bolt-glow"
                                d="M55 -2 L72 28 L55 32 L78 60 L58 64 L82 102"
                                fill="none"
                                stroke="rgba(255, 220, 100, 0.6)"
                                strokeWidth="9"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              {/* Main bolt — bright core. */}
                              <path
                                className="chip-panel__opt-bolt-main"
                                d="M55 -2 L72 28 L55 32 L78 60 L58 64 L82 102"
                                fill="none"
                                stroke="url(#bolt-gradient)"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              {/* Short branch off the middle for that
                                "forking" lightning feel. */}
                              <path
                                className="chip-panel__opt-bolt-branch"
                                d="M55 32 L40 48 L48 54"
                                fill="none"
                                stroke="url(#bolt-gradient)"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                          {/* Top-left: lightning-boost indicator —
                            Crazy-Time-style chunky italic multiplier.
                            No chassis: just bold white text with a
                            tier-coloured glow halo + dark stroke for
                            legibility against the marble. The text
                            scales by VALUE tier (legendary 20x is much
                            larger than a common 1.5x) so the magnitude
                            reads at a glance. Backend feeds
                            `lightning_boost_percent` as 0-1900; final
                            lightning factor = 1 + percent/100, clean
                            integers (x2, x5, x10, x20) or one decimal
                            (x1.2, x1.5) depending on tier. */}
                          {isBoosted &&
                            (() => {
                              const factor = 1 + lightningBoostPercent / 100
                              const text =
                                factor % 1 === 0 ? `x${factor.toFixed(0)}` : `x${factor.toFixed(1)}`
                              // Value-tier sizing (NOT boost-type tier — a
                              // common 2x and a rare 2x both get the
                              // medium size; common 1.5x stays small;
                              // legendary 20x goes huge).
                              const sizeClass =
                                factor >= 10
                                  ? 'chip-panel__opt-boost--size-mega'
                                  : factor >= 5
                                    ? 'chip-panel__opt-boost--size-big'
                                    : factor >= 2
                                      ? 'chip-panel__opt-boost--size-mid'
                                      : 'chip-panel__opt-boost--size-small'
                              return (
                                <span
                                  className={[
                                    'chip-panel__opt-boost',
                                    `chip-panel__opt-boost--${currentRace?.lightningBoostType ?? 'common'}`,
                                    sizeClass,
                                    isLightningRevealing ? 'chip-panel__opt-boost--revealing' : ''
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                >
                                  <span className="chip-panel__opt-boost-text">{text}</span>
                                </span>
                              )
                            })()}
                          {option.image ? (
                            <img
                              src={option.image}
                              alt={option.name}
                              className="chip-panel__opt-img"
                            />
                          ) : (
                            <div
                              className="chip-panel__opt-dot"
                              style={{ backgroundColor: option.color }}
                            />
                          )}
                          {/* Boundary label + multiplier grouped so the
                              compact short-phone row layout can stack them
                              (label over multiplier) beside the icon. On
                              desktop this is `display: contents` so the
                              column layout is unchanged (QA-60241). */}
                          <div className="chip-panel__opt-info">
                            {/* CCTV boundary label — in-flow under the
                            option's icon. Only on CCTV games
                            (irl/irl_alt/duck_river/snow_run) once
                            betting has closed. Tone-class derived
                            from the label format so each boundary
                            inherits the same colour as its option
                            type (Under=red / Range=blue / Over=green
                            / Jackpot=gold). */}
                            {showCCTVBoundaries &&
                              cctvOptionLabels[option.id] &&
                              (() => {
                                const label = cctvOptionLabels[option.id]!
                                const tone = label.startsWith('Under ')
                                  ? 'under'
                                  : label.startsWith('Over ')
                                    ? 'over'
                                    : label.includes(' or ')
                                      ? 'jackpot'
                                      : 'range'
                                return (
                                  <span
                                    className={`chip-panel__opt-boundary chip-panel__opt-boundary--${tone}`}
                                    aria-label={`Boundary: ${label}`}
                                  >
                                    {label}
                                  </span>
                                )
                              })()}

                            {/* Bottom-center multiplier — shows the FINAL
                            multiplier including any active lightning
                            + inventory boosts. Locale-agnostic on
                            purpose: just the marble image above + the
                            number here, no colour name + no "Pays"
                            text-label so the chip-placement option
                            board reads identically across languages.
                            The inventory-booster glyph stays inline
                            as a soft secondary indicator (different
                            from the top-left lightning indicator,
                            which is the round-level boost). */}
                            {(() => {
                              const optionBoosterPercent = appliedBoosters[option.id]?.percent ?? 0
                              const hasBooster = optionBoosterPercent > 0
                              const lightningMult = isBoosted ? 1 + lightningBoostPercent / 100 : 1
                              const inventoryMult = hasBooster ? 1 + optionBoosterPercent / 100 : 1
                              // Include the level-system bonus in the displayed
                              // multiplier so what the player sees matches the
                              // actual payout — backend applies the same factor
                              // unconditionally when LevelSystemEnabled is on.
                              const mult =
                                option.multiplier * lightningMult * inventoryMult * levelBoostFactor
                              return (
                                <span
                                  className={[
                                    'chip-panel__opt-pays',
                                    isBoosted ? 'chip-panel__opt-pays--boosted' : '',
                                    hasBooster ? 'chip-panel__opt-pays--booster' : '',
                                    isBoosted && isLightningRevealing
                                      ? 'chip-panel__opt-pays--bumping'
                                      : ''
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                >
                                  <span className="chip-panel__opt-pays-value">
                                    {/* Floor (not round) to 2dp so the shown odds
                                      never read higher than the floored potential
                                      win — a 7.3349x option used to show 7.34x
                                      while a $1 bet only paid 7.33 (QA-60088). */}
                                    {mult % 1 === 0
                                      ? mult.toFixed(0)
                                      : roundAmount(mult, 2).toFixed(2)}
                                    x
                                  </span>
                                  {hasBooster && (
                                    <LightningIcon className="chip-panel__opt-pays-booster-ico" />
                                  )}
                                </span>
                              )
                            })()}
                          </div>

                          {placed &&
                            (() => {
                              // Free-bet chips render as a distinct gold-rimmed
                              // "ticket" with a FREE label + the amount. They
                              // never stack (single inventory item per option),
                              // and the chip-color logic is bypassed entirely
                              // because the visual signal is "this is a free
                              // bet" not "this is a $25 stake".
                              const freeBet = appliedFreeBets[option.id]
                              if (freeBet) {
                                return (
                                  <div
                                    className={[
                                      'chip-panel__opt-chip',
                                      'chip-panel__opt-chip--top',
                                      'chip-panel__opt-chip--freebet',
                                      freshChips[option.id] ? 'chip-panel__opt-chip--entering' : '',
                                      myBetLost ? 'chip-panel__opt-chip--lost' : '',
                                      submissionStates[option.id] === 'confirmed'
                                        ? 'chip-panel__opt-chip--confirmed'
                                        : '',
                                      submissionStates[option.id] === 'failed'
                                        ? 'chip-panel__opt-chip--failed'
                                        : ''
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                                  >
                                    <span className="chip-panel__opt-chip-freebet-label">FREE</span>
                                    <span className="chip-panel__opt-chip-amount">
                                      {formatNumberWithSuffix(freeBet.amount, t)}
                                    </span>
                                    {!betsSubmitted && !isSettled && (
                                      <div
                                        className="chip-panel__opt-chip-x"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleRemoveChip(option.id)
                                        }}
                                      >
                                        ✕
                                      </div>
                                    )}
                                  </div>
                                )
                              }

                              const chipStyle = getChipStyleForAmount(placed.amount, chips)
                              // Visualise the click-history as a real chip
                              // pile: cap at 3 visible shadow layers so the
                              // pile reads as "this had several plays" without
                              // visually overflowing the chip slot. Each
                              // shadow layer is the same chip shape offset
                              // down/right behind the front chip, lower
                              // opacity so the "front" chip stays the read.
                              const shadowLayers = Math.max(0, Math.min(3, (placed.count ?? 1) - 1))
                              return (
                                <>
                                  {Array.from({ length: shadowLayers }).map((_, i) => {
                                    // Layer 1 sits closest to the front,
                                    // layer N (shadowLayers) is the deepest.
                                    const depth = shadowLayers - i
                                    return (
                                      <div
                                        key={`shadow-${i}`}
                                        className="chip-panel__opt-chip chip-panel__opt-chip--shadow"
                                        aria-hidden="true"
                                        style={
                                          {
                                            background: chipStyle.bg,
                                            borderColor: chipStyle.border,
                                            '--stack-depth': depth
                                          } as React.CSSProperties
                                        }
                                      />
                                    )
                                  })}
                                  <div
                                    className={[
                                      'chip-panel__opt-chip',
                                      'chip-panel__opt-chip--top',
                                      freshChips[option.id] ? 'chip-panel__opt-chip--entering' : '',
                                      doubleAnimAt > 0 ? 'chip-panel__opt-chip--doubling' : '',
                                      myBetLost ? 'chip-panel__opt-chip--lost' : '',
                                      submissionStates[option.id] === 'confirmed'
                                        ? 'chip-panel__opt-chip--confirmed'
                                        : '',
                                      submissionStates[option.id] === 'failed'
                                        ? 'chip-panel__opt-chip--failed'
                                        : ''
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                                    style={{
                                      background: chipStyle.bg,
                                      borderColor: chipStyle.border
                                    }}
                                  >
                                    <span className="chip-panel__opt-chip-amount">
                                      {formatNumberWithSuffix(placed.amount, t)}
                                    </span>
                                    {!betsSubmitted && !isSettled && (
                                      <div
                                        className="chip-panel__opt-chip-x"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleRemoveChip(option.id)
                                        }}
                                      >
                                        ✕
                                      </div>
                                    )}
                                  </div>
                                </>
                              )
                            })()}
                        </button>
                      )
                    })}
                </div>
              </div>
            </React.Fragment>
          ))}
      </div>

      {/* ── Configured side-bet markets ── e.g. Even/Odd, Exact-Boundary
          on CCTV games. Rendered as compact pills below the main
          tile grid using the same visual chassis as the derived
          forecast/tricast SideBetRows so the panel reads as one
          coherent side-bet rail. Each pill is one bettable option
          across all `type='side_bet'` markets; hidden options
          (e.g. the "Neither" bucket on Exact-Boundary that only
          exists to balance the probability config to 100%) are
          filtered out by `bettable: false`. Post-submit only the
          pills the player actually staked on stay visible — same
          behaviour as the forecast/tricast rail's --collapsed
          treatment so the rail doesn't sit empty during the race. */}
      {configuredSideBetOptions.length > 0 &&
        (() => {
          const visiblePills = configuredSideBetOptions.filter(
            ({ option }) => !betsSubmitted || !!placedChips[option.id]
          )
          if (visiblePills.length === 0) return null
          return (
            <div
              className="chip-panel__sidebets"
              data-count={visiblePills.length}
              aria-label="Side bets"
            >
              {visiblePills.map(({ market, option }, idx) => {
                const placed = placedChips[option.id]
                const chipStyle = placed ? getChipStyleForAmount(placed.amount, chips) : null
                const submission = submissionStates[option.id]
                const fresh = !!freshChips[option.id]

                const isBoosted = lightningBoostApplies && lightningBoostedItems.has(option.id)
                const optionBoosterPercent = appliedBoosters[option.id]?.percent ?? 0
                const displayedMultiplier =
                  option.multiplier *
                  (isBoosted ? 1 + lightningBoostPercent / 100 : 1) *
                  (optionBoosterPercent > 0 ? 1 + optionBoosterPercent / 100 : 1) *
                  levelBoostFactor

                const myBet = myBetByOptionId.get(option.id)
                const wonWithChip = isSettled && !!placed && myBet?.status === 'won'
                const lostWithChip = isSettled && !!placed && myBet?.status === 'lost'

                // Once race_started broadcasts the round's boundaries
                // (Phase A optionLabels payload), swap the static
                // "Exact Low" / "Exact High" placeholder for the
                // actual numbers ("Exact 12" / "Exact 14"). Even/Odd
                // doesn't carry a per-round label so it falls back to
                // the marble's static color value.
                // The fallback is the option's static name, which for these
                // markets IS the marble `color` from Supabase ("Exact Low" /
                // "Exact High") — data, not a source literal, so the i18n
                // extractor never sees it. It must go through the marbleColors
                // lookup or it renders English in every locale (QA: Turkish
                // player, 2026-08-28). The per-round label is numeric
                // ("Exact 12") and needs no translation.
                const dynamicLabel =
                  showCCTVBoundaries && cctvOptionLabels[option.id]
                    ? cctvOptionLabels[option.id]
                    : getTranslatedMarbleColor(option.name, t)

                // Use the persisted bet's payout when settled, same
                // pattern the forecast/tricast pills use. Falls back
                // to chip × multiplier if the bet hasn't propagated
                // yet (race condition between settled event and bet
                // store).
                const wonAmount = wonWithChip
                  ? myBet?.potentialPayout && myBet.potentialPayout > 0
                    ? myBet.potentialPayout
                    : (placed?.amount ?? 0) * displayedMultiplier
                  : 0

                return (
                  <button
                    key={option.id}
                    type="button"
                    className={[
                      'chip-panel__sidebet',
                      'chip-panel__sidebet--configured',
                      placed ? 'chip-panel__sidebet--placed' : '',
                      betsSubmitted ? 'chip-panel__sidebet--locked' : '',
                      wonWithChip ? 'chip-panel__sidebet--won' : '',
                      lostWithChip ? 'chip-panel__sidebet--lost' : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    disabled={betsSubmitted}
                    onClick={() => {
                      if (betsSubmitted) return
                      handleOptionClick(market.id, option.id)
                    }}
                    aria-label={`${dynamicLabel} at x${roundAmount(displayedMultiplier, 2).toFixed(2)}`}
                  >
                    <div className="chip-panel__sidebet-marbles">
                      <div
                        className="chip-panel__sidebet-marble"
                        style={{
                          background: option.image
                            ? `center / cover no-repeat url(${option.image})`
                            : option.color
                        }}
                      />
                    </div>
                    <div className="chip-panel__sidebet-label">{dynamicLabel}</div>
                    <div className="chip-panel__sidebet-pays">
                      {/* Floor to 2dp so the odds never exceed the floored
                          potential win (QA-60088). */}
                      {roundAmount(displayedMultiplier, 2).toFixed(2)}x
                    </div>
                    {/* Settle-time payout pill — same chassis the forecast/
                        tricast pills use. Only renders for placed wins
                        so reading the row tells you "I won this much". */}
                    {wonWithChip && (
                      <WinPill
                        isPayout
                        payoutAmount={wonAmount}
                        currencySymbol={currencySymbol}
                        tier="win"
                        delayMs={sideBetWinPillBaseDelayMs + idx * 80}
                      />
                    )}
                    <div className="chip-panel__sidebet-chip-slot" aria-hidden={!placed}>
                      {placed && chipStyle && (
                        <div
                          className={[
                            'chip-panel__opt-chip',
                            'chip-panel__opt-chip--top',
                            'chip-panel__opt-chip--sidebet',
                            fresh ? 'chip-panel__opt-chip--entering' : '',
                            submission === 'confirmed' ? 'chip-panel__opt-chip--confirmed' : '',
                            submission === 'failed' ? 'chip-panel__opt-chip--failed' : ''
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          style={{
                            background: chipStyle.bg,
                            borderColor: chipStyle.border
                          }}
                        >
                          <span className="chip-panel__opt-chip-amount">
                            {formatNumberWithSuffix(placed.amount, t)}
                          </span>
                          {!betsSubmitted && !isSettled && (
                            <div
                              className="chip-panel__opt-chip-x"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemoveChip(option.id)
                              }}
                            >
                              ✕
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )
        })()}

      {/* ── Forecast / Tricast side bets ── derived from the player's
          marble pick order in the main market. Hidden until the player
          has placed chips on at least 2 distinct main-market marbles.
          Side-bet placements share the chip-placement bet pipeline:
          the chip carries its `betType` + `selectedItems` snapshot, and
          `useChipPlacementBetSubmit` passes them straight through to
          the existing forecast/tricast backend code paths — no new
          markets or domain types required. */}
      {mainMarket && (
        <SideBetRows
          marbles={game.marbles}
          totalMarbles={totalActiveMarbles}
          gameKey={sideBetGameKey}
          maxResultPositions={game.resultsToSubmit}
          maxPickableMarbles={game.maxPickableMarbles}
          marbleOrder={marbleOrder}
          mainMarketId={mainMarket.id}
          placedChips={placedChips}
          betsSubmitted={betsSubmitted}
          isSettled={isSettled}
          placementByMarbleId={placementByOptionId}
          myBetByOptionId={mySideBetByOptionId}
          currency={currency}
          lightningBoostApplies={lightningBoostApplies}
          lightningBoostedItems={lightningBoostedItems}
          lightningBoostPercent={lightningBoostPercent}
          levelBoostFactor={levelBoostFactor}
          appliedBoosters={appliedBoosters}
          // Side-bet WinPills cascade in *after* the main option
          // wins. `totalWinDelayMs - PILL_COUNTUP_TAIL_MS` lands at
          // the end of the main cascade; we add a small +120ms gap
          // so the player's eye has a beat between the two waves.
          winPillBaseDelayMs={sideBetWinPillBaseDelayMs}
          submissionStates={submissionStates}
          freshChips={freshChips}
          onPlace={sideBetOnPlace}
          onRemove={handleRemoveChip}
          getChipStyleForAmount={sideBetGetChipStyle}
          formatAmount={sideBetFormatAmount}
        />
      )}

      {/* ── Chip bar ── extracted to `<ChipStrip>` (same markup, class names
          and SCSS) so the Birdie board can render the identical strip. The
          sound effects stay here: the strip is presentational, every
          mutation arrives as a prop. */}
      <ChipStrip
        chips={chips}
        selectedChipIdx={safeChipIdx}
        onSelectChip={(idx) => {
          setSelectedChipIdx(idx)
          playEffect('click')
        }}
        onUndo={() => {
          handleUndo()
          playEffect('click')
        }}
        onClear={() => {
          handleClearAllWithSave()
          playEffect('cancel')
        }}
        onDouble={() => {
          handleDouble()
          playEffect('click')
        }}
        onRepeat={() => {
          handleRepeat()
          playEffect('click')
        }}
        hasChips={chipCount > 0}
        canRepeat={lastBets !== null}
        hidden={betsSubmitted}
        doublePunchAt={doubleAnimAt}
        autoBet={autoBet}
        autoSpinAt={autoSpinAt}
        onToggleAutoBet={() => {
          setAutoBet((prev) => {
            const next = !prev
            showNotice(next ? t('Auto-bet on') : t('Auto-bet off'), 'success')
            return next
          })
          setAutoSpinAt(0)
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setAutoSpinAt(Date.now()))
          })
        }}
        hideAutoBet={isBirdieGame}
        barRef={chipSelectorRef}
      />

      {/* ── Flying chip animations (portaled to body) ── */}
      {typeof document !== 'undefined' &&
        flyingChips.length > 0 &&
        createPortal(
          <>
            {flyingChips.map((fly) => (
              <div
                key={fly.id}
                className="chip-panel__flying"
                style={
                  {
                    '--fly-from-x': `${fly.fromX}px`,
                    '--fly-from-y': `${fly.fromY}px`,
                    '--fly-to-x': `${fly.toX}px`,
                    '--fly-to-y': `${fly.toY}px`,
                    background: fly.bg,
                    borderColor: fly.border
                  } as React.CSSProperties
                }
              >
                <span>{formatNumberWithSuffix(fly.amount, t)}</span>
              </div>
            ))}
          </>,
          document.body
        )}
    </div>
  )
}
